import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../../components/Sidebar/Sidebar';
import Topbar from '../../components/Topbar/Topbar';
import RightSidebar from '../../components/RightSidebar/RightSidebar';
import './Settings.css';

import API_URL from '../../config';
import AutocompleteInput from '../../components/common/AutocompleteInput';
import { CITY_LIST, getDistricts } from '../../data/turkeyGeo';
import { UNIVERSITIES } from '../../data/turkeyUniversities';
import { ENGINEERING_DEPARTMENTS } from '../../data/engineeringDepartments';
import { ELECTRICAL_CERTIFICATES, CERT_CATEGORIES, ISSUERS, VISIBILITY_OPTIONS } from '../../data/electricalCertificates';
import { GENERAL_LICENSES, LICENSE_CATEGORIES } from '../../data/generalCertificates';
import { SOFTWARE_SKILLS, SKILL_CATEGORIES, SKILL_LEVELS } from '../../data/softwareSkills';
import { DISCIPLINE_PROJECT_TYPES, DISCIPLINE_PROJECT_TYPE_CATEGORIES } from '../../data/projectTypes';


const Settings = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [activeTab, setActiveTab] = useState('account');
  const [saved, setSaved] = useState(false);
  const [editSection, setEditSection] = useState(null);
  const [theme, setTheme] = useState(() => localStorage.getItem('projabze_theme') || 'dark');
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [avatarUploading, setAvatarUploading] = useState(false);
  const fileInputRef = useRef(null);

  const [account, setAccount] = useState({
    firstName: '', lastName: '', email: '', phone: '', birthDate: '',
  });
  const [passwords, setPasswords] = useState({
    current: '', newPass: '', confirm: '',
  });
  const [showPw, setShowPw] = useState({ current: false, newPass: false, confirm: false });

  const [author, setAuthor] = useState({
    title: '', fullName: '', diplomaNo: '', buroTescilNo: '',
    vergiNo: '', vergiDairesi: '', phone: '', email: '',
    chamberNo: '',
  });

  const [educationList, setEducationList] = useState([]);
  const [certificates, setCertificates] = useState('');
  const [certificateList, setCertificateList] = useState([]);
  const [certPicker, setCertPicker] = useState({ open: false, category: null, search: '' });
  const [experienceList, setExperienceList] = useState([]);
  const [licenseList, setLicenseList] = useState([]);
  const [licensePicker, setLicensePicker] = useState({ open: false, category: null, search: '' });
  const [skillList, setSkillList] = useState([]);
  const [skillPicker, setSkillPicker] = useState({ open: false, category: null, search: '' });
  const [projectTypeList, setProjectTypeList] = useState([]);
  const [projectTypePicker, setProjectTypePicker] = useState({ open: false, category: null, search: '' });

  const [company, setCompany] = useState({
    name: '', taxNo: '', taxOffice: '', address: '',
    city: '', district: '', postalCode: '', phone: '',
    email: '', website: '',
  });

  const [socialProfile, setSocialProfile] = useState({
    sectorRole: '', discipline: '', sectorInfo: {}, bio: '', city: '', district: '', linkedinUrl: '', twitterUrl: '', websiteUrl: ''
  });
  const [socialSaving, setSocialSaving] = useState(false);
  const [disciplineOptions, setDisciplineOptions] = useState([]);
  const [sectorRoleOptions, setSectorRoleOptions] = useState([]);
  const [openAcc, setOpenAcc] = useState({ discipline: true, roleInfo: true, socialLinks: true, xp: true, testimonial: false });

  // KVKK md. 11 — kendi verilerine erişim, indirme, silme
  const [myAuditLogs, setMyAuditLogs] = useState([]);
  const [myAuditTotal, setMyAuditTotal] = useState(0);
  const [erasureRequests, setErasureRequests] = useState([]);
  const [erasureReason, setErasureReason] = useState('');
  const [erasureBusy, setErasureBusy] = useState(false);
  const [erasureMsg, setErasureMsg] = useState('');
  const [dataExportBusy, setDataExportBusy] = useState(false);

  const loadDataTab = async () => {
    const tk = localStorage.getItem('projabze_token');
    if (!tk) return;
    try {
      const [logsRes, erasRes] = await Promise.all([
        fetch(`${API_URL}/api/me/audit-logs?limit=50`, { headers: { Authorization: `Bearer ${tk}` } }),
        fetch(`${API_URL}/api/me/erasure-request`, { headers: { Authorization: `Bearer ${tk}` } }),
      ]);
      if (logsRes.ok) {
        const d = await logsRes.json();
        setMyAuditLogs(d.logs || []);
        setMyAuditTotal(d.total || 0);
      }
      if (erasRes.ok) {
        const d = await erasRes.json();
        setErasureRequests(d.requests || []);
      }
    } catch (e) { /* ignore */ }
  };

  useEffect(() => {
    if (activeTab === 'data') loadDataTab();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const downloadMyData = async () => {
    if (dataExportBusy) return;
    setDataExportBusy(true);
    try {
      const tk = localStorage.getItem('projabze_token');
      const res = await fetch(`${API_URL}/api/me/data-export`, { headers: { Authorization: `Bearer ${tk}` } });
      if (!res.ok) throw new Error('export failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `projabze-verilerim-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      alert('Veri indirme başarısız oldu, lütfen tekrar deneyin.');
    } finally { setDataExportBusy(false); }
  };

  const submitErasureRequest = async () => {
    if (erasureBusy) return;
    if (!window.confirm('Hesabınızı silme talebi gönderilecek. Talep, en geç 30 gün içinde değerlendirilir. Onay sonrası kişisel verileriniz silinir; mali/yasal yükümlülük gerektiren kayıtlar (faturalar, audit log) yasal süre boyunca pseudonimize tutulur. Devam edilsin mi?')) return;
    setErasureBusy(true);
    setErasureMsg('');
    try {
      const tk = localStorage.getItem('projabze_token');
      const res = await fetch(`${API_URL}/api/me/erasure-request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${tk}` },
        body: JSON.stringify({ reason: erasureReason }),
      });
      const d = await res.json();
      if (res.ok) {
        setErasureMsg('Talebiniz alındı. 30 gün içinde sonuçlandırılacaktır.');
        setErasureReason('');
        loadDataTab();
      } else {
        setErasureMsg(d.message || 'Talep gönderilemedi.');
      }
    } catch (e) {
      setErasureMsg('Bağlantı hatası.');
    } finally { setErasureBusy(false); }
  };

  // Anasayfa yorumu (testimonial) — KVKK rıza ile yayın
  const [testimonial, setTestimonial] = useState(null);     // { id, stars, text, status, ... } | null
  const [testimonialDraft, setTestimonialDraft] = useState({ stars: 5, text: '', publishConsent: false });
  const [testimonialBusy, setTestimonialBusy] = useState(false);
  const [testimonialMsg, setTestimonialMsg] = useState('');
  useEffect(() => {
    const tk = localStorage.getItem('projabze_token');
    if (!tk) return;
    fetch(`${API_URL}/api/testimonials/me`, { headers: { Authorization: `Bearer ${tk}` } })
      .then(r => r.ok ? r.json() : null)
      .then(d => {
        if (d?.testimonial) {
          setTestimonial(d.testimonial);
          setTestimonialDraft({ stars: d.testimonial.stars, text: d.testimonial.text, publishConsent: !!d.testimonial.publishConsent });
        }
      })
      .catch(() => {});
  }, []);
  const submitTestimonial = async () => {
    if (testimonialBusy) return;
    if (!testimonialDraft.publishConsent) {
      setTestimonialMsg('Yayınlama açık rızası gerekli.');
      return;
    }
    setTestimonialBusy(true);
    setTestimonialMsg('');
    try {
      const tk = localStorage.getItem('projabze_token');
      const res = await fetch(`${API_URL}/api/testimonials`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${tk}` },
        body: JSON.stringify(testimonialDraft),
      });
      const data = await res.json();
      if (res.ok) {
        setTestimonial(data.testimonial);
        setTestimonialMsg('Gönderildi. Onaylandıktan sonra anasayfada yayınlanacak.');
      } else {
        setTestimonialMsg(data.message || 'Gönderilemedi.');
      }
    } catch { setTestimonialMsg('Sunucu hatası.'); }
    setTestimonialBusy(false);
  };
  const deleteTestimonial = async () => {
    if (!testimonial) return;
    if (!window.confirm('Yorumunuzu silip rızanızı geri çekmek istiyor musunuz?')) return;
    try {
      const tk = localStorage.getItem('projabze_token');
      const res = await fetch(`${API_URL}/api/testimonials/${testimonial.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${tk}` },
      });
      if (res.ok) {
        setTestimonial(null);
        setTestimonialDraft({ stars: 5, text: '', publishConsent: false });
        setTestimonialMsg('Yorum kaldırıldı.');
      }
    } catch {}
  };
  const toggleAcc = (key) => setOpenAcc(s => ({ ...s, [key]: !s[key] }));

  useEffect(() => {
    const stored = localStorage.getItem('projabze_user');
    if (stored) {
      const u = JSON.parse(stored);
      setUser(u);
      setAccount({
        firstName: u.firstName || '',
        lastName: u.lastName || '',
        email: u.email || '',
        phone: u.phone || '',
        birthDate: u.birthDate || '',
      });
      const uid = u.id || u.email;
      setSocialProfile({
        sectorRole: u.sectorRole || '',
        discipline: u.discipline || '',
        sectorInfo: u.sectorInfo || {},
        bio: u.bio || '',
        city: u.city || '',
        district: u.district || '',
        linkedinUrl: u.linkedinUrl || '',
        twitterUrl: u.twitterUrl || '',
        websiteUrl: u.websiteUrl || ''
      });
      const savedAuthor = localStorage.getItem(`projabze_author_${uid}`);
      if (savedAuthor) setAuthor(JSON.parse(savedAuthor));
      const savedCompany = localStorage.getItem(`projabze_company_${uid}`);
      if (savedCompany) setCompany(JSON.parse(savedCompany));
      const savedEducation = localStorage.getItem(`projabze_education_${uid}`);
      if (savedEducation) {
        const parsed = JSON.parse(savedEducation);
        if (Array.isArray(parsed.list)) setEducationList(parsed.list);
        if (parsed.certificates) setCertificates(parsed.certificates);
        if (Array.isArray(parsed.certificateList)) setCertificateList(parsed.certificateList);
        if (Array.isArray(parsed.experienceList)) setExperienceList(parsed.experienceList);
        if (Array.isArray(parsed.licenseList)) setLicenseList(parsed.licenseList);
        if (Array.isArray(parsed.skillList)) setSkillList(parsed.skillList);
        if (Array.isArray(parsed.projectTypeList)) setProjectTypeList(parsed.projectTypeList);
      }
      // Sunucudan da çek; varsa (kullanıcı başka tarayıcıdan/cihazdan kaydetmiş olabilir)
      // localStorage üstüne yazılır. Hata olursa local fallback kullanılır.
      const tkCv = localStorage.getItem('projabze_token');
      if (tkCv && uid) {
        fetch(`${API_URL}/api/social/users/${uid}/profile`, { headers: { Authorization: `Bearer ${tkCv}` } })
          .then(r => r.ok ? r.json() : null)
          .then(data => {
            const cv = data?.cvBundle;
            if (!cv) return;
            if (Array.isArray(cv.educationList)) setEducationList(cv.educationList);
            if (typeof cv.certificates === 'string') setCertificates(cv.certificates);
            if (Array.isArray(cv.certificateList)) setCertificateList(cv.certificateList);
            if (Array.isArray(cv.experienceList)) setExperienceList(cv.experienceList);
            if (Array.isArray(cv.licenseList)) setLicenseList(cv.licenseList);
            if (Array.isArray(cv.skillList)) setSkillList(cv.skillList);
            if (Array.isArray(cv.projectTypeList)) setProjectTypeList(cv.projectTypeList);
          })
          .catch(() => {});
      }
    } else {
      navigate('/');
    }
  }, [navigate]);

  // Disciplines + sector roles from admin config
  useEffect(() => {
    const token = localStorage.getItem('projabze_token');
    if (!token) return;
    fetch(`${API_URL}/api/disciplines/config`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (data?.disciplines) {
          setDisciplineOptions(data.disciplines.map(d => ({ id: d.id, name: d.name, code: d.code })));
        }
      })
      .catch(() => {});
    fetch(`${API_URL}/api/social/sector-roles`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (data?.items) setSectorRoleOptions(data.items);
      })
      .catch(() => {});
  }, []);

  const toggleTheme = () => {
    const next = theme === 'dark' ? 'light' : 'dark';
    setTheme(next);
    localStorage.setItem('projabze_theme', next);
  };

  const handleLogout = () => {
    localStorage.removeItem('projabze_token');
    localStorage.removeItem('projabze_user');
    navigate('/');
  };

  const showSaved = () => {
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  };

  const handleAvatarClick = () => {
    fileInputRef.current?.click();
  };

  const handleAvatarChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Preview
    const reader = new FileReader();
    reader.onload = (ev) => setAvatarPreview(ev.target.result);
    reader.readAsDataURL(file);

    // Upload
    setAvatarUploading(true);
    try {
      const token = localStorage.getItem('projabze_token');
      const formData = new FormData();
      formData.append('avatar', file);

      const res = await fetch(`${API_URL}/api/upload/avatar`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      const data = await res.json();
      if (res.ok) {
        const updated = { ...user, avatar: data.avatar };
        setUser(updated);
        localStorage.setItem('projabze_user', JSON.stringify(updated));
        showSaved();
      } else {
        alert(data.message || 'Yüklenemedi.');
        setAvatarPreview(null);
      }
    } catch {
      alert('Baglanti hatasi.');
      setAvatarPreview(null);
    } finally {
      setAvatarUploading(false);
      e.target.value = '';
    }
  };

  const handleAvatarDelete = async () => {
    const token = localStorage.getItem('projabze_token');
    try {
      const res = await fetch(`${API_URL}/api/upload/avatar`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        setAvatarPreview(null);
        const updated = { ...user, avatar: '' };
        setUser(updated);
        localStorage.setItem('projabze_user', JSON.stringify(updated));
        showSaved();
      }
    } catch {
      alert('Baglanti hatasi.');
    }
  };

  const avatarSrc = avatarPreview || (user?.avatar ? `${API_URL}${user.avatar}` : null);

  const saveAccount = (e) => {
    e.preventDefault();
    const updated = { ...user, ...account };
    localStorage.setItem('projabze_user', JSON.stringify(updated));
    setUser(updated);
    setEditSection(null);
    showSaved();
  };

  const savePassword = (e) => {
    e.preventDefault();
    if (passwords.newPass !== passwords.confirm) {
      alert('Yeni şifreler eşleşmiyor.');
      return;
    }
    if (passwords.newPass.length < 6) {
      alert('Şifre en az 6 karakter olmalıdır.');
      return;
    }
    setPasswords({ current: '', newPass: '', confirm: '' });
    setEditSection(null);
    showSaved();
  };

  const uid = user?.id || user?.email;

  const saveAuthor = (e) => {
    e.preventDefault();
    localStorage.setItem(`projabze_author_${uid}`, JSON.stringify(author));
    setEditSection(null);
    showSaved();
  };

  const saveCompany = (e) => {
    e.preventDefault();
    localStorage.setItem(`projabze_company_${uid}`, JSON.stringify(company));
    setEditSection(null);
    showSaved();
  };

  const eduLevels = [
    { id: 'lise', label: 'Lise' },
    { id: 'onlisans', label: 'Önlisans' },
    { id: 'lisans', label: 'Lisans' },
    { id: 'yukseklisans', label: 'Yüksek Lisans' },
    { id: 'doktora', label: 'Doktora' },
  ];

  const emptyEdu = (level) => ({
    level,
    school: '',
    faculty: '',
    department: '',
    graduationYear: '',
    gpa: '',
  });

  const addEducation = (level) => {
    setEducationList([...educationList, emptyEdu(level)]);
  };

  const updateEducation = (index, field, value) => {
    const updated = [...educationList];
    updated[index] = { ...updated[index], [field]: value };
    setEducationList(updated);
  };

  const removeEducation = (index) => {
    setEducationList(educationList.filter((_, i) => i !== index));
  };

  const persistEducationBundle = (patch = {}) => {
    const bundle = {
      list: educationList,
      certificates,
      certificateList,
      experienceList,
      licenseList,
      skillList,
      projectTypeList,
      ...patch,
    };
    localStorage.setItem(`projabze_education_${uid}`, JSON.stringify(bundle));
    // Sunucuya da gönder — diğer kullanıcılar profil sayfasında görebilsin.
    const tk = localStorage.getItem('projabze_token');
    if (tk) {
      const payload = {
        educationList: bundle.list,
        certificates: bundle.certificates,
        certificateList: bundle.certificateList,
        experienceList: bundle.experienceList,
        licenseList: bundle.licenseList,
        skillList: bundle.skillList,
        projectTypeList: bundle.projectTypeList,
      };
      fetch(`${API_URL}/api/social/users/me/cv`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${tk}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      }).catch(err => console.warn('CV sunucuya gönderilemedi:', err));
    }
  };

  const saveEducation = (e) => {
    e.preventDefault();
    persistEducationBundle();
    setEditSection(null);
    showSaved();
  };

  const saveCertificates = (e) => {
    e.preventDefault();
    persistEducationBundle();
    setEditSection(null);
    showSaved();
  };

  // ─── Structured certificates helpers ───
  const persistCerts = (nextList) => {
    setCertificateList(nextList);
    persistEducationBundle({ certificateList: nextList });
  };

  // ─── Experience helpers ───
  const persistExperiences = (nextList) => {
    setExperienceList(nextList);
    persistEducationBundle({ experienceList: nextList });
  };

  const addExperience = () => {
    const newExp = {
      id: `exp-${Date.now()}`,
      position: '',
      company: '',
      location: '',
      startDate: '',
      endDate: '',
      current: false,
      description: '',
    };
    persistExperiences([...experienceList, newExp]);
    setEditSection(`exp-${newExp.id}`);
  };

  const updateExperience = (id, patch) => {
    persistExperiences(experienceList.map(e => e.id === id ? { ...e, ...patch } : e));
  };

  const removeExperience = (id) => {
    persistExperiences(experienceList.filter(e => e.id !== id));
  };

  // ─── License / general cert helpers ───
  const persistLicenses = (nextList) => {
    setLicenseList(nextList);
    persistEducationBundle({ licenseList: nextList });
  };

  const addLicenseFromPreset = (preset) => {
    const newLic = {
      id: `${preset.id}-${Date.now()}`,
      presetId: preset.id,
      name: preset.name,
      category: preset.category || '',
      scope: preset.scope || '',
      issuer: preset.issuer || '',
      date: '',
      expiry: '',
      documentNo: '',
      notes: '',
    };
    persistLicenses([...licenseList, newLic]);
    setEditSection(`lic-${newLic.id}`);
    setLicensePicker({ open: false, category: null, search: '' });
  };

  const addLicenseCustom = () => {
    const newLic = {
      id: `lic-${Date.now()}`,
      presetId: null,
      name: '',
      category: '',
      scope: '',
      issuer: '',
      date: '',
      expiry: '',
      documentNo: '',
      notes: '',
    };
    persistLicenses([...licenseList, newLic]);
    setEditSection(`lic-${newLic.id}`);
    setLicensePicker({ open: false, category: null, search: '' });
  };

  const updateLicense = (id, patch) => {
    persistLicenses(licenseList.map(l => l.id === id ? { ...l, ...patch } : l));
  };

  const removeLicense = (id) => {
    persistLicenses(licenseList.filter(l => l.id !== id));
  };

  // ─── Software skill helpers ───
  const persistSkills = (nextList) => {
    setSkillList(nextList);
    persistEducationBundle({ skillList: nextList });
  };

  const addSkillFromPreset = (preset) => {
    if (skillList.some(s => s.presetId === preset.id)) {
      setSkillPicker({ open: false, category: null, search: '' });
      return;
    }
    const newSkill = {
      id: `${preset.id}-${Date.now()}`,
      presetId: preset.id,
      name: preset.name,
      category: preset.category || '',
      level: 'intermediate',
      years: '',
    };
    persistSkills([...skillList, newSkill]);
    setSkillPicker({ open: false, category: null, search: '' });
  };

  const addSkillCustom = () => {
    const newSkill = {
      id: `skill-${Date.now()}`,
      presetId: null,
      name: '',
      category: '',
      level: 'intermediate',
      years: '',
    };
    persistSkills([...skillList, newSkill]);
    setEditSection(`skill-${newSkill.id}`);
    setSkillPicker({ open: false, category: null, search: '' });
  };

  const updateSkill = (id, patch) => {
    persistSkills(skillList.map(s => s.id === id ? { ...s, ...patch } : s));
  };

  const removeSkill = (id) => {
    persistSkills(skillList.filter(s => s.id !== id));
  };

  // ─── Project type helpers ───
  const persistProjectTypes = (nextList) => {
    setProjectTypeList(nextList);
    persistEducationBundle({ projectTypeList: nextList });
  };

  const addProjectTypeFromPreset = (preset) => {
    if (projectTypeList.some(p => p.presetId === preset.id)) {
      setProjectTypePicker({ open: false, category: null, search: '' });
      return;
    }
    const newPt = {
      id: `${preset.id}-${Date.now()}`,
      presetId: preset.id,
      name: preset.name,
      category: preset.category || '',
      count: '',
      years: '',
      notes: '',
    };
    persistProjectTypes([...projectTypeList, newPt]);
    setProjectTypePicker({ open: false, category: null, search: '' });
  };

  const addProjectTypeCustom = () => {
    const newPt = {
      id: `pt-${Date.now()}`,
      presetId: null,
      name: '',
      category: '',
      count: '',
      years: '',
      notes: '',
    };
    persistProjectTypes([...projectTypeList, newPt]);
    setEditSection(`pt-${newPt.id}`);
    setProjectTypePicker({ open: false, category: null, search: '' });
  };

  const updateProjectType = (id, patch) => {
    persistProjectTypes(projectTypeList.map(p => p.id === id ? { ...p, ...patch } : p));
  };

  const removeProjectType = (id) => {
    persistProjectTypes(projectTypeList.filter(p => p.id !== id));
  };

  const addCertificateFromPreset = (preset) => {
    const newCert = {
      id: `${preset.id}-${Date.now()}`,
      presetId: preset.id,
      name: preset.name,
      issuer: preset.issuer || '',
      category: preset.category || '',
      scope: preset.scope || '',
      date: '',
      expiry: '',
      validityMonths: preset.validityMonths || null,
      renewalYearly: !!preset.renewalYearly,
      documentNo: '',
      verifyUrl: preset.verifyUrl || '',
      fileUrl: '',
      visibility: 'public',
      notes: preset.note || '',
    };
    persistCerts([...certificateList, newCert]);
    setEditSection(`cert-${newCert.id}`);
    setCertPicker({ open: false, category: null, search: '' });
  };

  const addCertificateCustom = () => {
    const newCert = {
      id: `custom-${Date.now()}`,
      presetId: null,
      name: '',
      issuer: '',
      category: '',
      scope: '',
      date: '',
      expiry: '',
      validityMonths: null,
      renewalYearly: false,
      documentNo: '',
      verifyUrl: '',
      fileUrl: '',
      visibility: 'public',
      notes: '',
    };
    persistCerts([...certificateList, newCert]);
    setEditSection(`cert-${newCert.id}`);
    setCertPicker({ open: false, category: null, search: '' });
  };

  const updateCertificate = (id, patch) => {
    persistCerts(certificateList.map(c => c.id === id ? { ...c, ...patch } : c));
  };

  const removeCertificate = (id) => {
    persistCerts(certificateList.filter(c => c.id !== id));
  };

  const usedLevels = educationList.map(e => e.level);
  const availableLevels = eduLevels.filter(l => !usedLevels.includes(l.id));

  if (!user) return null;

  const initials = (user.firstName?.[0] || '') + (user.lastName?.[0] || '');
  const memberSince = user.createdAt
    ? new Date(user.createdAt).toLocaleDateString('tr-TR', { year: 'numeric', month: 'long' })
    : 'Mart 2026';

  const settingsNav = [
    { id: 'account', label: 'Hesap Bilgileri' },
    { id: 'social', label: 'Sosyal Profil' },
    { id: 'company', label: 'Firma Bilgileri' },
    { id: 'education', label: 'Eğitim Bilgileri' },
    { id: 'cv', label: 'Özgeçmiş' },
    { id: 'security', label: 'Güvenlik & Gizlilik' },
    { id: 'data', label: 'Hesap Verilerim (KVKK)' },
  ];

  return (
    <div className={`settings-page ${theme}`}>
      <Topbar user={user} onLogout={handleLogout} />

      {/* ─── LEFT SIDEBAR ─── */}
      <Sidebar activeId="settings" user={user} onLogout={handleLogout} />

      {/* ─── MAIN ─── */}
      <main className="stg-main">
        {/* Toast */}
        {saved && <div className="stg-toast"><span className="stg-toast-icon">&#10003;</span> Degisiklikler kaydedildi</div>}

        {/* Profile Banner */}
        <div className="stg-banner">
          <div className="stg-banner-bg">
            <button className="stg-theme-toggle" onClick={toggleTheme} title={theme === 'dark' ? 'Aydinlik Mod' : 'Karanlik Mod'}>
              <span className="stg-theme-track">
                <span className="stg-theme-thumb">
                  {theme === 'dark' ? '\u{1F319}' : '\u{2600}\u{FE0F}'}
                </span>
              </span>
              <span className="stg-theme-label">{theme === 'dark' ? 'Karanlik' : 'Aydinlik'}</span>
            </button>
          </div>
          <div className="stg-banner-content">
            <div className="stg-avatar-wrapper">
              <div className="stg-banner-avatar" onClick={handleAvatarClick}>
                {avatarSrc ? (
                  <img src={avatarSrc} alt="Profil" className="stg-avatar-img" />
                ) : (
                  initials
                )}
                <div className="stg-avatar-overlay">
                  {avatarUploading ? (
                    <span className="stg-avatar-spinner"></span>
                  ) : (
                    <span className="stg-avatar-cam">&#128247;</span>
                  )}
                </div>
              </div>
              {avatarSrc && !avatarUploading && (
                <button className="stg-avatar-delete" onClick={handleAvatarDelete} title="Fotografi kaldir">
                  &times;
                </button>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                onChange={handleAvatarChange}
                style={{ display: 'none' }}
              />
            </div>
            <div className="stg-banner-info">
              <h1>{user.firstName} {user.lastName}</h1>
              <p className="stg-banner-role">Elektrik Proje Mühendisi</p>
              <p className="stg-banner-meta">Uye: {memberSince} &middot; Ucretsiz Plan</p>
            </div>
          </div>
        </div>

        {/* Two-column layout: nav + content */}
        <div className="stg-layout">
          {/* Settings Navigation */}
          <div className="stg-settings-nav">
            <div className="stg-card">
              {settingsNav.map(item => (
                <button
                  key={item.id}
                  className={`stg-settings-nav-item ${activeTab === item.id ? 'active' : ''}`}
                  onClick={() => { setActiveTab(item.id); setEditSection(null); }}
                >
                  {item.label}
                  <span className="stg-nav-arrow">&rsaquo;</span>
                </button>
              ))}
            </div>
          </div>

          {/* Content */}
          <div className="stg-content">

            {/* ── ACCOUNT ── */}
            {activeTab === 'account' && (
              <>
                {/* Personal Info Card */}
                <div className="stg-card">
                  <div className="stg-card-header">
                    <h2>Kişisel Bilgiler</h2>
                    {editSection !== 'personal' && (
                      <button className="stg-edit-btn" onClick={() => setEditSection('personal')}>
                        <span>&#9998;</span> Düzenle
                      </button>
                    )}
                  </div>
                  {editSection === 'personal' ? (
                    <form onSubmit={saveAccount}>
                      <div className="stg-form-grid">
                        <div className="stg-field">
                          <label>Ad</label>
                          <input type="text" value={account.firstName} onChange={e => setAccount({ ...account, firstName: e.target.value })} />
                        </div>
                        <div className="stg-field">
                          <label>Soyad</label>
                          <input type="text" value={account.lastName} onChange={e => setAccount({ ...account, lastName: e.target.value })} />
                        </div>
                        <div className="stg-field">
                          <label>E-posta</label>
                          <input type="email" value={account.email} onChange={e => setAccount({ ...account, email: e.target.value })} />
                        </div>
                        <div className="stg-field">
                          <label>Telefon</label>
                          <input type="tel" value={account.phone} onChange={e => setAccount({ ...account, phone: e.target.value })} placeholder="0 (5XX) XXX XX XX" />
                        </div>
                        <div className="stg-field">
                          <label>Doğum Tarihi</label>
                          <input type="date" value={account.birthDate || ''} onChange={e => setAccount({ ...account, birthDate: e.target.value })} />
                        </div>
                      </div>
                      <div className="stg-card-actions">
                        <button type="submit" className="stg-btn-primary">Kaydet</button>
                        <button type="button" className="stg-btn-ghost" onClick={() => {
                          setEditSection(null);
                          setAccount({ firstName: user.firstName || '', lastName: user.lastName || '', email: user.email || '', phone: user.phone || '', birthDate: user.birthDate || '' });
                        }}>İptal</button>
                      </div>
                    </form>
                  ) : (
                    <div className="stg-display-grid">
                      <div className="stg-display-item">
                        <span className="stg-display-label">Ad</span>
                        <span className="stg-display-value">{account.firstName || '-'}</span>
                      </div>
                      <div className="stg-display-item">
                        <span className="stg-display-label">Soyad</span>
                        <span className="stg-display-value">{account.lastName || '-'}</span>
                      </div>
                      <div className="stg-display-item">
                        <span className="stg-display-label">E-posta</span>
                        <span className="stg-display-value">{account.email || '-'}</span>
                      </div>
                      <div className="stg-display-item">
                        <span className="stg-display-label">Telefon</span>
                        <span className="stg-display-value">{account.phone || 'Henüz eklenmedi'}</span>
                      </div>
                      <div className="stg-display-item">
                        <span className="stg-display-label">Doğum Tarihi</span>
                        <span className="stg-display-value">{account.birthDate || 'Henüz eklenmedi'}</span>
                      </div>
                    </div>
                  )}
                </div>

                {/* Membership Card */}
                <div className="stg-card">
                  <div className="stg-card-header">
                    <h2>Üyelik</h2>
                  </div>
                  <div className="stg-membership">
                    <div className="stg-membership-badge">
                      <span className="stg-membership-icon">&#9733;</span>
                      <div>
                        <strong>Ucretsiz Plan</strong>
                        <p>3 proje, 100 MB depolama</p>
                      </div>
                    </div>
                    <div className="stg-membership-bar-wrap">
                      <div className="stg-membership-bar">
                        <div className="stg-membership-fill" style={{ width: '0%' }}></div>
                      </div>
                      <span className="stg-membership-text">0 / 3 proje kullanildi</span>
                    </div>
                    <button className="stg-btn-upgrade">Plani Yukselt</button>
                  </div>
                </div>
              </>
            )}

            {/* ── AUTHOR ── */}
            {activeTab === 'author' && (
              <>
                <div className="stg-card">
                  <div className="stg-card-header">
                    <div>
                      <h2>Müellif Bilgileri</h2>
                      <p className="stg-card-desc">Proje paftaları ve teknik raporlarda görünecek bilgiler</p>
                    </div>
                    {editSection !== 'author' && (
                      <button className="stg-edit-btn" onClick={() => setEditSection('author')}>
                        <span>&#9998;</span> Düzenle
                      </button>
                    )}
                  </div>
                  {editSection === 'author' ? (
                    <form onSubmit={saveAuthor}>
                      <div className="stg-form-grid">
                        <div className="stg-field">
                          <label>Unvan</label>
                          <select value={author.title} onChange={e => setAuthor({ ...author, title: e.target.value })}>
                            <option value="">Seçiniz</option>
                            <option value="Elk. Müh.">Elk. Muh.</option>
                            <option value="Elk. Yük. Müh.">Elk. Yuk. Muh.</option>
                            <option value="Elk.-Elk. Müh.">Elk.-Elk. Muh.</option>
                            <option value="Elk. Teknikeri">Elk. Teknikeri</option>
                          </select>
                        </div>
                        <div className="stg-field">
                          <label>Ad Soyad</label>
                          <input type="text" value={author.fullName} onChange={e => setAuthor({ ...author, fullName: e.target.value })} placeholder="Proje müellifi ad soyad" />
                        </div>
                        <div className="stg-field">
                          <label>Diploma No</label>
                          <input type="text" value={author.diplomaNo} onChange={e => setAuthor({ ...author, diplomaNo: e.target.value })} placeholder="Diploma numarasi" />
                        </div>
                        <div className="stg-field">
                          <label>Buro Tescil No</label>
                          <input type="text" value={author.buroTescilNo} onChange={e => setAuthor({ ...author, buroTescilNo: e.target.value })} placeholder="Buro tescil numarasi" />
                        </div>
                        <div className="stg-field">
                          <label>Oda Sicil No</label>
                          <input type="text" value={author.chamberNo} onChange={e => setAuthor({ ...author, chamberNo: e.target.value })} placeholder="EMO sicil numarasi" />
                        </div>
                      </div>
                      <div className="stg-card-actions">
                        <button type="submit" className="stg-btn-primary">Kaydet</button>
                        <button type="button" className="stg-btn-ghost" onClick={() => setEditSection(null)}>İptal</button>
                      </div>
                    </form>
                  ) : (
                    <div className="stg-display-grid">
                      <div className="stg-display-item">
                        <span className="stg-display-label">Unvan</span>
                        <span className="stg-display-value">{author.title || 'Henüz eklenmedi'}</span>
                      </div>
                      <div className="stg-display-item">
                        <span className="stg-display-label">Ad Soyad</span>
                        <span className="stg-display-value">{author.fullName || 'Henüz eklenmedi'}</span>
                      </div>
                      <div className="stg-display-item">
                        <span className="stg-display-label">Diploma No</span>
                        <span className="stg-display-value">{author.diplomaNo || 'Henüz eklenmedi'}</span>
                      </div>
                      <div className="stg-display-item">
                        <span className="stg-display-label">Buro Tescil No</span>
                        <span className="stg-display-value">{author.buroTescilNo || 'Henüz eklenmedi'}</span>
                      </div>
                      <div className="stg-display-item">
                        <span className="stg-display-label">Oda Sicil No</span>
                        <span className="stg-display-value">{author.chamberNo || 'Henüz eklenmedi'}</span>
                      </div>
                    </div>
                  )}
                </div>

                {/* Tax Info Card */}
                <div className="stg-card">
                  <div className="stg-card-header">
                    <h2>Vergi & İletişim</h2>
                    {editSection !== 'author-tax' && (
                      <button className="stg-edit-btn" onClick={() => setEditSection('author-tax')}>
                        <span>&#9998;</span> Düzenle
                      </button>
                    )}
                  </div>
                  {editSection === 'author-tax' ? (
                    <form onSubmit={saveAuthor}>
                      <div className="stg-form-grid">
                        <div className="stg-field">
                          <label>Vergi No / T.C. Kimlik No</label>
                          <input type="text" value={author.vergiNo} onChange={e => setAuthor({ ...author, vergiNo: e.target.value })} placeholder="T.C. Kimlik No / Vergi No" />
                        </div>
                        <div className="stg-field">
                          <label>Vergi Dairesi</label>
                          <input type="text" value={author.vergiDairesi} onChange={e => setAuthor({ ...author, vergiDairesi: e.target.value })} placeholder="Vergi dairesi adi" />
                        </div>
                        <div className="stg-field">
                          <label>Telefon</label>
                          <input type="tel" value={author.phone} onChange={e => setAuthor({ ...author, phone: e.target.value })} placeholder="0 (5XX) XXX XX XX" />
                        </div>
                        <div className="stg-field">
                          <label>E-posta</label>
                          <input type="email" value={author.email} onChange={e => setAuthor({ ...author, email: e.target.value })} placeholder="muellif@email.com" />
                        </div>
                      </div>
                      <div className="stg-card-actions">
                        <button type="submit" className="stg-btn-primary">Kaydet</button>
                        <button type="button" className="stg-btn-ghost" onClick={() => setEditSection(null)}>İptal</button>
                      </div>
                    </form>
                  ) : (
                    <div className="stg-display-grid">
                      <div className="stg-display-item">
                        <span className="stg-display-label">Vergi No</span>
                        <span className="stg-display-value">{author.vergiNo || 'Henüz eklenmedi'}</span>
                      </div>
                      <div className="stg-display-item">
                        <span className="stg-display-label">Vergi Dairesi</span>
                        <span className="stg-display-value">{author.vergiDairesi || 'Henüz eklenmedi'}</span>
                      </div>
                      <div className="stg-display-item">
                        <span className="stg-display-label">Telefon</span>
                        <span className="stg-display-value">{author.phone || 'Henüz eklenmedi'}</span>
                      </div>
                      <div className="stg-display-item">
                        <span className="stg-display-label">E-posta</span>
                        <span className="stg-display-value">{author.email || 'Henüz eklenmedi'}</span>
                      </div>
                    </div>
                  )}
                </div>

                <div className="stg-info-banner">
                  <span className="stg-info-banner-icon">&#8505;</span>
                  <span>Bu bilgiler proje paftaları, teknik raporlar ve resmi belgelerinizde müellif olarak görünecektir.</span>
                </div>
              </>
            )}

            {/* ── COMPANY ── */}
            {activeTab === 'company' && (
              <>
                <div className="stg-card">
                  <div className="stg-card-header">
                    <div>
                      <h2>Firma Bilgileri</h2>
                      <p className="stg-card-desc">Proje kapaklarinda ve resmi yazismalarda gorunecek bilgiler</p>
                    </div>
                    {editSection !== 'company-main' && (
                      <button className="stg-edit-btn" onClick={() => setEditSection('company-main')}>
                        <span>&#9998;</span> Düzenle
                      </button>
                    )}
                  </div>
                  {editSection === 'company-main' ? (
                    <form onSubmit={saveCompany}>
                      <div className="stg-form-grid">
                        <div className="stg-field full">
                          <label>Firma Adı</label>
                          <input type="text" value={company.name} onChange={e => setCompany({ ...company, name: e.target.value })} placeholder="Firma / Buro adi" />
                        </div>
                        <div className="stg-field">
                          <label>Vergi No</label>
                          <input type="text" value={company.taxNo} onChange={e => setCompany({ ...company, taxNo: e.target.value })} placeholder="Firma vergi numarasi" />
                        </div>
                        <div className="stg-field">
                          <label>Vergi Dairesi</label>
                          <input type="text" value={company.taxOffice} onChange={e => setCompany({ ...company, taxOffice: e.target.value })} placeholder="Vergi dairesi" />
                        </div>
                      </div>
                      <div className="stg-card-actions">
                        <button type="submit" className="stg-btn-primary">Kaydet</button>
                        <button type="button" className="stg-btn-ghost" onClick={() => setEditSection(null)}>İptal</button>
                      </div>
                    </form>
                  ) : (
                    <div className="stg-display-grid">
                      <div className="stg-display-item full">
                        <span className="stg-display-label">Firma Adı</span>
                        <span className="stg-display-value">{company.name || 'Henüz eklenmedi'}</span>
                      </div>
                      <div className="stg-display-item">
                        <span className="stg-display-label">Vergi No</span>
                        <span className="stg-display-value">{company.taxNo || 'Henüz eklenmedi'}</span>
                      </div>
                      <div className="stg-display-item">
                        <span className="stg-display-label">Vergi Dairesi</span>
                        <span className="stg-display-value">{company.taxOffice || 'Henüz eklenmedi'}</span>
                      </div>
                    </div>
                  )}
                </div>

                {/* Address Card */}
                <div className="stg-card">
                  <div className="stg-card-header">
                    <h2>Adres & İletişim</h2>
                    {editSection !== 'company-addr' && (
                      <button className="stg-edit-btn" onClick={() => setEditSection('company-addr')}>
                        <span>&#9998;</span> Düzenle
                      </button>
                    )}
                  </div>
                  {editSection === 'company-addr' ? (
                    <form onSubmit={saveCompany}>
                      <div className="stg-form-grid">
                        <div className="stg-field full">
                          <label>Adres</label>
                          <textarea rows={3} value={company.address} onChange={e => setCompany({ ...company, address: e.target.value })} placeholder="Firma adresi" />
                        </div>
                        <div className="stg-field">
                          <label>İl</label>
                          <AutocompleteInput
                            value={company.city}
                            onChange={val => {
                              setCompany(prev => ({ ...prev, city: val, ...(val !== prev.city ? { district: '' } : {}) }));
                            }}
                            options={CITY_LIST}
                            placeholder="İl seçin veya yazın..."
                          />
                        </div>
                        <div className="stg-field">
                          <label>İlçe</label>
                          <AutocompleteInput
                            value={company.district}
                            onChange={val => setCompany(prev => ({ ...prev, district: val }))}
                            options={getDistricts(company.city)}
                            placeholder={company.city ? 'İlçe seçin veya yazın...' : 'Önce il seçin...'}
                          />
                        </div>
                        <div className="stg-field">
                          <label>Posta Kodu</label>
                          <input type="text" value={company.postalCode} onChange={e => setCompany({ ...company, postalCode: e.target.value })} placeholder="Posta kodu" />
                        </div>
                        <div className="stg-field">
                          <label>Telefon</label>
                          <input type="tel" value={company.phone} onChange={e => setCompany({ ...company, phone: e.target.value })} placeholder="0 (XXX) XXX XX XX" />
                        </div>
                        <div className="stg-field">
                          <label>E-posta</label>
                          <input type="email" value={company.email} onChange={e => setCompany({ ...company, email: e.target.value })} placeholder="info@firma.com" />
                        </div>
                        <div className="stg-field">
                          <label>Web Sitesi</label>
                          <input type="url" value={company.website} onChange={e => setCompany({ ...company, website: e.target.value })} placeholder="https://firma.com" />
                        </div>
                      </div>
                      <div className="stg-card-actions">
                        <button type="submit" className="stg-btn-primary">Kaydet</button>
                        <button type="button" className="stg-btn-ghost" onClick={() => setEditSection(null)}>İptal</button>
                      </div>
                    </form>
                  ) : (
                    <div className="stg-display-grid">
                      <div className="stg-display-item full">
                        <span className="stg-display-label">Adres</span>
                        <span className="stg-display-value">{company.address || 'Henüz eklenmedi'}</span>
                      </div>
                      <div className="stg-display-item">
                        <span className="stg-display-label">İl / İlçe</span>
                        <span className="stg-display-value">{company.city && company.district ? `${company.city} / ${company.district}` : 'Henüz eklenmedi'}</span>
                      </div>
                      <div className="stg-display-item">
                        <span className="stg-display-label">Posta Kodu</span>
                        <span className="stg-display-value">{company.postalCode || 'Henüz eklenmedi'}</span>
                      </div>
                      <div className="stg-display-item">
                        <span className="stg-display-label">Telefon</span>
                        <span className="stg-display-value">{company.phone || 'Henüz eklenmedi'}</span>
                      </div>
                      <div className="stg-display-item">
                        <span className="stg-display-label">E-posta</span>
                        <span className="stg-display-value">{company.email || 'Henüz eklenmedi'}</span>
                      </div>
                      <div className="stg-display-item">
                        <span className="stg-display-label">Web Sitesi</span>
                        <span className="stg-display-value">{company.website || 'Henüz eklenmedi'}</span>
                      </div>
                    </div>
                  )}
                </div>

                <div className="stg-info-banner">
                  <span className="stg-info-banner-icon">&#8505;</span>
                  <span>Firma bilgileri proje kapak sayfalari ve resmi yazismalarda kullanilacaktir.</span>
                </div>
              </>
            )}

            {/* ── EDUCATION ── */}
            {activeTab === 'education' && (
              <>
                {/* Existing education entries */}
                {educationList.map((edu, idx) => {
                  const levelLabel = eduLevels.find(l => l.id === edu.level)?.label || edu.level;
                  const isLise = edu.level === 'lise';
                  const editKey = `edu-${idx}`;
                  return (
                    <div className="stg-card" key={idx}>
                      <div className="stg-card-header">
                        <div>
                          <h2>{levelLabel}</h2>
                          <p className="stg-card-desc">{levelLabel} eğitim bilgileri</p>
                        </div>
                        <div style={{ display: 'flex', gap: '.4rem' }}>
                          {editSection !== editKey && (
                            <button className="stg-edit-btn" onClick={() => setEditSection(editKey)}>
                              <span>&#9998;</span> Düzenle
                            </button>
                          )}
                          <button className="stg-btn-danger" style={{ padding: '.4rem .7rem', fontSize: '.72rem' }} onClick={() => { removeEducation(idx); showSaved(); }}>
                            Kaldir
                          </button>
                        </div>
                      </div>
                      {editSection === editKey ? (
                        <form onSubmit={saveEducation}>
                          <div className="stg-form-grid">
                            <div className="stg-field full">
                              <label>{isLise ? 'Okul Adi' : 'Universite'}</label>
                              {isLise ? (
                                <input type="text" value={edu.school} onChange={e => updateEducation(idx, 'school', e.target.value)} placeholder="Lise adi" />
                              ) : (
                                <AutocompleteInput
                                  value={edu.school}
                                  onChange={val => updateEducation(idx, 'school', val)}
                                  options={UNIVERSITIES}
                                  placeholder="Üniversite seçin veya yazın..."
                                />
                              )}
                            </div>
                            {!isLise && (
                              <>
                                <div className="stg-field">
                                  <label>Fakulte</label>
                                  <input type="text" value={edu.faculty} onChange={e => updateEducation(idx, 'faculty', e.target.value)} placeholder="Fakulte adi" />
                                </div>
                                <div className="stg-field">
                                  <label>Bolum</label>
                                  <AutocompleteInput
                                    value={edu.department}
                                    onChange={val => updateEducation(idx, 'department', val)}
                                    options={ENGINEERING_DEPARTMENTS}
                                    placeholder="Bölüm seçin veya yazın..."
                                  />
                                </div>
                              </>
                            )}
                            {isLise && (
                              <div className="stg-field">
                                <label>Bolum / Alan</label>
                                <input type="text" value={edu.department} onChange={e => updateEducation(idx, 'department', e.target.value)} placeholder="orn. Sayisal, Elektrik-Elektronik" />
                              </div>
                            )}
                            <div className="stg-field">
                              <label>Mezuniyet Yili</label>
                              <input type="text" value={edu.graduationYear} onChange={e => updateEducation(idx, 'graduationYear', e.target.value)} placeholder="orn. 2020" />
                            </div>
                            {!isLise && (
                              <div className="stg-field">
                                <label>GNO</label>
                                <input type="text" value={edu.gpa} onChange={e => updateEducation(idx, 'gpa', e.target.value)} placeholder="orn. 3.25" />
                              </div>
                            )}
                          </div>
                          <div className="stg-card-actions">
                            <button type="submit" className="stg-btn-primary">Kaydet</button>
                            <button type="button" className="stg-btn-ghost" onClick={() => setEditSection(null)}>İptal</button>
                          </div>
                        </form>
                      ) : (
                        <div className="stg-display-grid">
                          <div className="stg-display-item full">
                            <span className="stg-display-label">{isLise ? 'Okul' : 'Universite'}</span>
                            <span className="stg-display-value">{edu.school || 'Henüz eklenmedi'}</span>
                          </div>
                          {!isLise && (
                            <>
                              <div className="stg-display-item">
                                <span className="stg-display-label">Fakulte</span>
                                <span className="stg-display-value">{edu.faculty || 'Henüz eklenmedi'}</span>
                              </div>
                              <div className="stg-display-item">
                                <span className="stg-display-label">Bolum</span>
                                <span className="stg-display-value">{edu.department || 'Henüz eklenmedi'}</span>
                              </div>
                            </>
                          )}
                          {isLise && (
                            <div className="stg-display-item">
                              <span className="stg-display-label">Bolum / Alan</span>
                              <span className="stg-display-value">{edu.department || 'Henüz eklenmedi'}</span>
                            </div>
                          )}
                          <div className="stg-display-item">
                            <span className="stg-display-label">Mezuniyet Yili</span>
                            <span className="stg-display-value">{edu.graduationYear || 'Henüz eklenmedi'}</span>
                          </div>
                          {!isLise && (
                            <div className="stg-display-item">
                              <span className="stg-display-label">GNO</span>
                              <span className="stg-display-value">{edu.gpa || 'Henüz eklenmedi'}</span>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}

                {/* Add education button */}
                {availableLevels.length > 0 && (
                  <div className="stg-card stg-edu-add-card">
                    <p className="stg-edu-add-label">Eğitim Ekle:</p>
                    <div className="stg-edu-add-buttons">
                      {availableLevels.map(level => (
                        <button key={level.id} className="stg-btn-outline" onClick={() => addEducation(level.id)}>
                          + {level.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {educationList.length === 0 && (
                  <div className="stg-info-banner">
                    <span className="stg-info-banner-icon">&#8505;</span>
                    <span>Henüz eğitim bilgisi eklenmedi. Yukarıdaki butonlarla ekleyebilirsiniz.</span>
                  </div>
                )}

                {/* Experiences Card */}
                <div className="stg-card">
                  <div className="stg-card-header">
                    <div>
                      <h2>Deneyimler</h2>
                      <p className="stg-card-desc">İş deneyimlerinizi kronolojik sırada ekleyin — özgeçmişte otomatik görünür</p>
                    </div>
                    <button className="stg-btn-outline" onClick={addExperience}>+ Deneyim Ekle</button>
                  </div>
                  {experienceList.length === 0 ? (
                    <div className="stg-info-banner">
                      <span className="stg-info-banner-icon">&#8505;</span>
                      <span>Henüz deneyim eklenmedi. "+ Deneyim Ekle" ile başlayabilirsiniz.</span>
                    </div>
                  ) : (
                    <div className="stg-exp-list">
                      {experienceList.map(exp => {
                        const isEditing = editSection === `exp-${exp.id}`;
                        return (
                          <div key={exp.id} className="stg-exp-item">
                            {isEditing ? (
                              <div className="stg-exp-edit">
                                <div className="stg-form-grid">
                                  <div className="stg-field">
                                    <label>Pozisyon / Görev *</label>
                                    <input type="text" value={exp.position} onChange={e => updateExperience(exp.id, { position: e.target.value })} placeholder="örn: Elektrik Proje Mühendisi" />
                                  </div>
                                  <div className="stg-field">
                                    <label>Firma / Kurum *</label>
                                    <input type="text" value={exp.company} onChange={e => updateExperience(exp.id, { company: e.target.value })} placeholder="örn: ABC Mühendislik A.Ş." />
                                  </div>
                                  <div className="stg-field">
                                    <label>Konum</label>
                                    <input type="text" value={exp.location} onChange={e => updateExperience(exp.id, { location: e.target.value })} placeholder="örn: İstanbul / Türkiye" />
                                  </div>
                                  <div className="stg-field">
                                    <label>Başlangıç</label>
                                    <input type="month" value={exp.startDate || ''} onChange={e => updateExperience(exp.id, { startDate: e.target.value })} />
                                  </div>
                                  <div className="stg-field">
                                    <label>Bitiş {exp.current ? '(Devam ediyor)' : ''}</label>
                                    <input type="month" value={exp.endDate || ''} onChange={e => updateExperience(exp.id, { endDate: e.target.value })} disabled={exp.current} />
                                  </div>
                                  <div className="stg-field" style={{ display: 'flex', alignItems: 'center' }}>
                                    <label style={{ display: 'flex', alignItems: 'center', gap: '.4rem', cursor: 'pointer' }}>
                                      <input type="checkbox" checked={!!exp.current} onChange={e => updateExperience(exp.id, { current: e.target.checked, endDate: e.target.checked ? '' : exp.endDate })} />
                                      Hâlâ çalışıyorum
                                    </label>
                                  </div>
                                  <div className="stg-field full">
                                    <label>Açıklama</label>
                                    <textarea rows={3} value={exp.description} onChange={e => updateExperience(exp.id, { description: e.target.value })} placeholder="Sorumluluklar, projeler, başarılar..." />
                                  </div>
                                </div>
                                <div className="stg-card-actions">
                                  <button className="stg-btn-primary" onClick={() => { setEditSection(null); showSaved(); }}>Kaydet</button>
                                  <button className="stg-btn-danger" onClick={() => { removeExperience(exp.id); setEditSection(null); }}>Sil</button>
                                  <button className="stg-btn-ghost" onClick={() => setEditSection(null)}>Kapat</button>
                                </div>
                              </div>
                            ) : (
                              <div className="stg-exp-view">
                                <div className="stg-exp-main">
                                  <div className="stg-exp-head">
                                    <strong>{exp.position || '(Pozisyon yok)'}</strong>
                                    <span className="stg-exp-period">
                                      {exp.startDate || '—'}{' → '}{exp.current ? 'Devam' : (exp.endDate || '—')}
                                    </span>
                                  </div>
                                  <div className="stg-exp-sub">
                                    {exp.company && <span>{exp.company}</span>}
                                    {exp.location && <span>· {exp.location}</span>}
                                  </div>
                                  {exp.description && <p className="stg-exp-desc">{exp.description}</p>}
                                </div>
                                <div className="stg-cert-actions">
                                  <button className="stg-edit-btn" onClick={() => setEditSection(`exp-${exp.id}`)}>
                                    <span>&#9998;</span> Düzenle
                                  </button>
                                  <button className="stg-btn-danger" style={{ padding: '.35rem .65rem', fontSize: '.72rem' }} onClick={() => removeExperience(exp.id)}>
                                    Kaldır
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Yazılım Yetkinlikleri Card */}
                <div className="stg-card">
                  <div className="stg-card-header">
                    <div>
                      <h2>Yazılım & Teknik Yetkinlikler</h2>
                      <p className="stg-card-desc">AutoCAD, Revit, DIALux, ETAP, Bina Proje (bProje), Office ve diğer araçlar — seviyesiyle birlikte</p>
                    </div>
                    <div style={{ display: 'flex', gap: '.4rem', flexWrap: 'wrap' }}>
                      <button className="stg-btn-outline" onClick={() => setSkillPicker({ open: true, category: null, search: '' })}>
                        + Listeden Seç
                      </button>
                      <button className="stg-btn-outline" onClick={addSkillCustom}>
                        + Manuel Ekle
                      </button>
                    </div>
                  </div>

                  {skillPicker.open && (
                    <div className="stg-cert-picker">
                      <div className="stg-cert-picker-toolbar">
                        <input
                          type="text"
                          placeholder="Yazılım ara... (örn: AutoCAD, DIALux, Excel, ETAP)"
                          value={skillPicker.search}
                          onChange={e => setSkillPicker(p => ({ ...p, search: e.target.value }))}
                          className="stg-cert-search"
                          autoFocus
                        />
                        <select
                          value={skillPicker.category || ''}
                          onChange={e => setSkillPicker(p => ({ ...p, category: e.target.value || null }))}
                          className="stg-cert-filter"
                        >
                          <option value="">Tüm kategoriler</option>
                          {SKILL_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                        </select>
                        <button className="stg-btn-ghost" onClick={() => setSkillPicker({ open: false, category: null, search: '' })}>Kapat</button>
                      </div>
                      <div className="stg-cert-picker-body">
                        {SKILL_CATEGORIES
                          .filter(cat => !skillPicker.category || cat === skillPicker.category)
                          .map(cat => {
                            const q = skillPicker.search.trim().toLowerCase();
                            const items = SOFTWARE_SKILLS.filter(s => s.category === cat && (!q || s.name.toLowerCase().includes(q)));
                            if (items.length === 0) return null;
                            return (
                              <div key={cat} className="stg-cert-group">
                                <div className="stg-cert-group-title">{cat}</div>
                                <div className="stg-cert-group-items">
                                  {items.map(preset => {
                                    const already = skillList.some(s => s.presetId === preset.id);
                                    return (
                                      <button
                                        key={preset.id}
                                        className="stg-cert-option"
                                        disabled={already}
                                        onClick={() => addSkillFromPreset(preset)}
                                        style={already ? { opacity: .5, cursor: 'not-allowed' } : {}}
                                      >
                                        <span className="stg-cert-option-name">{preset.name}</span>
                                        {already && <span className="stg-cert-option-issuer">Eklendi</span>}
                                      </button>
                                    );
                                  })}
                                </div>
                              </div>
                            );
                          })}
                      </div>
                    </div>
                  )}

                  {skillList.length === 0 && !skillPicker.open ? (
                    <div className="stg-info-banner">
                      <span className="stg-info-banner-icon">&#8505;</span>
                      <span>Henüz yetkinlik eklenmedi. "Listeden Seç" ile AutoCAD, DIALux, Excel vb. tek tıkla ekleyebilirsiniz.</span>
                    </div>
                  ) : (
                    <div className="stg-skill-chips">
                      {skillList.map(skill => {
                        const isEditing = editSection === `skill-${skill.id}`;
                        const levelLabel = SKILL_LEVELS.find(l => l.value === skill.level)?.label || 'Orta';
                        return isEditing ? (
                          <div key={skill.id} className="stg-skill-edit">
                            <div className="stg-form-grid">
                              <div className="stg-field">
                                <label>Ad *</label>
                                <input type="text" value={skill.name} onChange={e => updateSkill(skill.id, { name: e.target.value })} />
                              </div>
                              <div className="stg-field">
                                <label>Kategori</label>
                                <select value={skill.category || ''} onChange={e => updateSkill(skill.id, { category: e.target.value })}>
                                  <option value="">Seçin...</option>
                                  {SKILL_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                                </select>
                              </div>
                              <div className="stg-field">
                                <label>Seviye</label>
                                <select value={skill.level || 'intermediate'} onChange={e => updateSkill(skill.id, { level: e.target.value })}>
                                  {SKILL_LEVELS.map(l => <option key={l.value} value={l.value}>{l.label}</option>)}
                                </select>
                              </div>
                              <div className="stg-field">
                                <label>Kaç Yıldır Kullanıyor</label>
                                <input type="number" min="0" value={skill.years || ''} onChange={e => updateSkill(skill.id, { years: e.target.value })} placeholder="örn: 5" />
                              </div>
                            </div>
                            <div className="stg-card-actions">
                              <button className="stg-btn-primary" onClick={() => { setEditSection(null); showSaved(); }}>Kaydet</button>
                              <button className="stg-btn-danger" onClick={() => { removeSkill(skill.id); setEditSection(null); }}>Sil</button>
                              <button className="stg-btn-ghost" onClick={() => setEditSection(null)}>Kapat</button>
                            </div>
                          </div>
                        ) : (
                          <div key={skill.id} className={`stg-skill-chip stg-skill-lv-${skill.level || 'intermediate'}`} onClick={() => setEditSection(`skill-${skill.id}`)}>
                            <span className="stg-skill-name">{skill.name}</span>
                            <span className="stg-skill-level">{levelLabel}</span>
                            {skill.years && <span className="stg-skill-years">{skill.years} yıl</span>}
                            <button className="stg-skill-remove" onClick={e => { e.stopPropagation(); removeSkill(skill.id); }} title="Kaldır">&times;</button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Proje Tecrübeleri Card */}
                <div className="stg-card">
                  <div className="stg-card-header">
                    <div>
                      <h2>Proje Tecrübeleri</h2>
                      <p className="stg-card-desc">İç tesisat, trafo, ENH, GES, hakediş vb. çalıştığınız proje tiplerini ekleyin — adet ve süre opsiyonel</p>
                    </div>
                    <div style={{ display: 'flex', gap: '.4rem', flexWrap: 'wrap' }}>
                      <button className="stg-btn-outline" onClick={() => setProjectTypePicker({ open: true, category: null, search: '' })}>
                        + Listeden Seç
                      </button>
                      <button className="stg-btn-outline" onClick={addProjectTypeCustom}>
                        + Manuel Ekle
                      </button>
                    </div>
                  </div>

                  {projectTypePicker.open && (
                    <div className="stg-cert-picker">
                      <div className="stg-cert-picker-toolbar">
                        <input
                          type="text"
                          placeholder="Proje tipi ara... (örn: trafo, ENH, hakediş, yüksek yapı)"
                          value={projectTypePicker.search}
                          onChange={e => setProjectTypePicker(p => ({ ...p, search: e.target.value }))}
                          className="stg-cert-search"
                          autoFocus
                        />
                        <select
                          value={projectTypePicker.category || ''}
                          onChange={e => setProjectTypePicker(p => ({ ...p, category: e.target.value || null }))}
                          className="stg-cert-filter"
                        >
                          <option value="">Tüm kategoriler</option>
                          {DISCIPLINE_PROJECT_TYPE_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                        </select>
                        <button className="stg-btn-ghost" onClick={() => setProjectTypePicker({ open: false, category: null, search: '' })}>Kapat</button>
                      </div>
                      <div className="stg-cert-picker-body">
                        {DISCIPLINE_PROJECT_TYPE_CATEGORIES
                          .filter(cat => !projectTypePicker.category || cat === projectTypePicker.category)
                          .map(cat => {
                            const q = projectTypePicker.search.trim().toLowerCase();
                            const items = DISCIPLINE_PROJECT_TYPES.filter(p => p.category === cat && (!q || p.name.toLowerCase().includes(q)));
                            if (items.length === 0) return null;
                            return (
                              <div key={cat} className="stg-cert-group">
                                <div className="stg-cert-group-title">{cat}</div>
                                <div className="stg-cert-group-items">
                                  {items.map(preset => {
                                    const already = projectTypeList.some(p => p.presetId === preset.id);
                                    return (
                                      <button
                                        key={preset.id}
                                        className="stg-cert-option"
                                        disabled={already}
                                        onClick={() => addProjectTypeFromPreset(preset)}
                                        style={already ? { opacity: .5, cursor: 'not-allowed' } : {}}
                                      >
                                        <span className="stg-cert-option-name">{preset.name}</span>
                                        {already && <span className="stg-cert-option-issuer">Eklendi</span>}
                                      </button>
                                    );
                                  })}
                                </div>
                              </div>
                            );
                          })}
                      </div>
                    </div>
                  )}

                  {projectTypeList.length === 0 && !projectTypePicker.open ? (
                    <div className="stg-info-banner">
                      <span className="stg-info-banner-icon">&#8505;</span>
                      <span>Henüz proje tecrübesi eklenmedi. "Listeden Seç" ile 90+ tipik elektrik projesi arasından seçim yapabilirsiniz.</span>
                    </div>
                  ) : (
                    <div className="stg-cert-list">
                      {projectTypeList.map(pt => {
                        const isEditing = editSection === `pt-${pt.id}`;
                        return (
                          <div key={pt.id} className="stg-cert-item">
                            {isEditing ? (
                              <div className="stg-cert-edit">
                                <div className="stg-form-grid">
                                  <div className="stg-field full">
                                    <label>Proje Tipi *</label>
                                    <input type="text" value={pt.name} onChange={e => updateProjectType(pt.id, { name: e.target.value })} placeholder="örn: Trafo Projesi — Tip Bina" />
                                  </div>
                                  <div className="stg-field">
                                    <label>Kategori</label>
                                    <select value={pt.category || ''} onChange={e => updateProjectType(pt.id, { category: e.target.value })}>
                                      <option value="">Seçin...</option>
                                      {DISCIPLINE_PROJECT_TYPE_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                                    </select>
                                  </div>
                                  <div className="stg-field">
                                    <label>Proje Adedi (yaklaşık)</label>
                                    <input type="number" min="0" value={pt.count || ''} onChange={e => updateProjectType(pt.id, { count: e.target.value })} placeholder="örn: 12" />
                                  </div>
                                  <div className="stg-field">
                                    <label>Deneyim Yılı</label>
                                    <input type="number" min="0" value={pt.years || ''} onChange={e => updateProjectType(pt.id, { years: e.target.value })} placeholder="örn: 4" />
                                  </div>
                                  <div className="stg-field full">
                                    <label>Notlar</label>
                                    <textarea rows={2} value={pt.notes || ''} onChange={e => updateProjectType(pt.id, { notes: e.target.value })} placeholder="Dikkat çekici projeler, ölçek, özellikler..." />
                                  </div>
                                </div>
                                <div className="stg-card-actions">
                                  <button className="stg-btn-primary" onClick={() => { setEditSection(null); showSaved(); }}>Kaydet</button>
                                  <button className="stg-btn-danger" onClick={() => { removeProjectType(pt.id); setEditSection(null); }}>Sil</button>
                                  <button className="stg-btn-ghost" onClick={() => setEditSection(null)}>Kapat</button>
                                </div>
                              </div>
                            ) : (
                              <div className="stg-cert-view">
                                <div className="stg-cert-main">
                                  <div className="stg-cert-title-row">
                                    <strong>{pt.name || '(Proje tipi yok)'}</strong>
                                    {pt.category && <span className="stg-cert-cat-badge">{pt.category}</span>}
                                  </div>
                                  <div className="stg-cert-meta">
                                    {pt.count && <span>{pt.count} proje</span>}
                                    {pt.years && <span>· {pt.years} yıl deneyim</span>}
                                  </div>
                                  {pt.notes && <p className="stg-cert-notes">{pt.notes}</p>}
                                </div>
                                <div className="stg-cert-actions">
                                  <button className="stg-edit-btn" onClick={() => setEditSection(`pt-${pt.id}`)}>
                                    <span>&#9998;</span> Düzenle
                                  </button>
                                  <button className="stg-btn-danger" style={{ padding: '.35rem .65rem', fontSize: '.72rem' }} onClick={() => removeProjectType(pt.id)}>
                                    Kaldır
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Structured Certificates Card */}
                <div className="stg-card">
                  <div className="stg-card-header">
                    <div>
                      <h2>Sertifikalar & Yetki Belgeleri</h2>
                      <p className="stg-card-desc">EMO eğitimleri, işletme sorumluluğu, MYK belgeleri ve diğer mesleki yetkinlikler</p>
                    </div>
                    <div style={{ display: 'flex', gap: '.4rem', flexWrap: 'wrap' }}>
                      <button className="stg-btn-outline" onClick={() => setCertPicker({ open: true, category: null, search: '' })}>
                        + Listeden Seç
                      </button>
                      <button className="stg-btn-outline" onClick={addCertificateCustom}>
                        + Manuel Ekle
                      </button>
                    </div>
                  </div>

                  {/* Picker modal / drawer */}
                  {certPicker.open && (
                    <div className="stg-cert-picker">
                      <div className="stg-cert-picker-toolbar">
                        <input
                          type="text"
                          placeholder="Sertifika ara... (örn: topraklama, asansör, YG)"
                          value={certPicker.search}
                          onChange={e => setCertPicker(p => ({ ...p, search: e.target.value }))}
                          className="stg-cert-search"
                          autoFocus
                        />
                        <select
                          value={certPicker.category || ''}
                          onChange={e => setCertPicker(p => ({ ...p, category: e.target.value || null }))}
                          className="stg-cert-filter"
                        >
                          <option value="">Tüm kategoriler</option>
                          {CERT_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                        </select>
                        <button className="stg-btn-ghost" onClick={() => setCertPicker({ open: false, category: null, search: '' })}>Kapat</button>
                      </div>
                      <div className="stg-cert-picker-body">
                        {CERT_CATEGORIES
                          .filter(cat => !certPicker.category || cat === certPicker.category)
                          .map(cat => {
                            const q = certPicker.search.trim().toLowerCase();
                            const items = ELECTRICAL_CERTIFICATES.filter(c => c.category === cat && (!q || c.name.toLowerCase().includes(q) || (c.issuer || '').toLowerCase().includes(q)));
                            if (items.length === 0) return null;
                            return (
                              <div key={cat} className="stg-cert-group">
                                <div className="stg-cert-group-title">{cat}</div>
                                <div className="stg-cert-group-items">
                                  {items.map(preset => (
                                    <button key={preset.id} className="stg-cert-option" onClick={() => addCertificateFromPreset(preset)}>
                                      <span className="stg-cert-option-name">{preset.name}</span>
                                      {preset.issuer && <span className="stg-cert-option-issuer">{preset.issuer}</span>}
                                    </button>
                                  ))}
                                </div>
                              </div>
                            );
                          })}
                      </div>
                    </div>
                  )}

                  {/* Certificate list */}
                  {certificateList.length === 0 && !certPicker.open ? (
                    <div className="stg-info-banner">
                      <span className="stg-info-banner-icon">&#8505;</span>
                      <span>Henüz sertifika eklenmedi. "Listeden Seç" ile EMO/MYK/İSG vb. hazır şablonlardan ekleyebilir veya "Manuel Ekle" ile özel bir belge tanımlayabilirsiniz.</span>
                    </div>
                  ) : (
                    <div className="stg-cert-list">
                      {certificateList.map((cert) => {
                        const isEditing = editSection === `cert-${cert.id}`;
                        const expiryStatus = cert.expiry ? (new Date(cert.expiry) < new Date() ? 'expired' : 'valid') : null;
                        return (
                          <div key={cert.id} className={`stg-cert-item${expiryStatus === 'expired' ? ' expired' : ''}`}>
                            {isEditing ? (
                              <div className="stg-cert-edit">
                                <div className="stg-form-grid">
                                  <div className="stg-field full">
                                    <label>Sertifika / Eğitim Adı *</label>
                                    <input type="text" value={cert.name} onChange={e => updateCertificate(cert.id, { name: e.target.value })} placeholder="örn: YG İşletme Sorumluluğu" />
                                  </div>
                                  <div className="stg-field">
                                    <label>Veren Kurum</label>
                                    <select value={cert.issuer || ''} onChange={e => updateCertificate(cert.id, { issuer: e.target.value })}>
                                      <option value="">Seçin...</option>
                                      {ISSUERS.map(i => <option key={i.key} value={i.key}>{i.label}</option>)}
                                    </select>
                                  </div>
                                  <div className="stg-field">
                                    <label>Kategori</label>
                                    <select value={cert.category || ''} onChange={e => updateCertificate(cert.id, { category: e.target.value })}>
                                      <option value="">Seçin...</option>
                                      {CERT_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                                    </select>
                                  </div>
                                  <div className="stg-field full">
                                    <label>Seviye / Kapsam</label>
                                    <input type="text" value={cert.scope || ''} onChange={e => updateCertificate(cert.id, { scope: e.target.value })} placeholder="örn: Seviye 4, 36 kV'a kadar, 1 kV altı+üstü" />
                                  </div>
                                  <div className="stg-field">
                                    <label>Veriliş Tarihi</label>
                                    <input type="date" value={cert.date || ''} onChange={e => updateCertificate(cert.id, { date: e.target.value })} />
                                  </div>
                                  <div className="stg-field">
                                    <label>Son Geçerlilik{cert.validityMonths ? ` (${cert.validityMonths} ay önerilir)` : ''}</label>
                                    <input type="date" value={cert.expiry || ''} onChange={e => updateCertificate(cert.id, { expiry: e.target.value })} />
                                  </div>
                                  <div className="stg-field">
                                    <label>Belge / Sicil No</label>
                                    <input type="text" value={cert.documentNo || ''} onChange={e => updateCertificate(cert.id, { documentNo: e.target.value })} placeholder="örn: 12345/2024" />
                                  </div>
                                  <div className="stg-field">
                                    <label>Gizlilik</label>
                                    <select value={cert.visibility || 'public'} onChange={e => updateCertificate(cert.id, { visibility: e.target.value })}>
                                      {VISIBILITY_OPTIONS.map(v => <option key={v.value} value={v.value}>{v.label}</option>)}
                                    </select>
                                  </div>
                                  <div className="stg-field full">
                                    <label>Doğrulama Linki (e-Devlet / EMO / MYK portal)</label>
                                    <input type="url" value={cert.verifyUrl || ''} onChange={e => updateCertificate(cert.id, { verifyUrl: e.target.value })} placeholder="https://turkiye.gov.tr ..." />
                                  </div>
                                  <div className="stg-field full">
                                    <label>Belge Dosyası (URL)</label>
                                    <input type="url" value={cert.fileUrl || ''} onChange={e => updateCertificate(cert.id, { fileUrl: e.target.value })} placeholder="PDF veya görüntü URL'si" />
                                  </div>
                                  <div className="stg-field full">
                                    <label>Notlar</label>
                                    <textarea rows={2} value={cert.notes || ''} onChange={e => updateCertificate(cert.id, { notes: e.target.value })} placeholder="Ek açıklama, kapsam, eğitim süresi vb." />
                                  </div>
                                </div>
                                <div className="stg-card-actions">
                                  <button className="stg-btn-primary" onClick={() => { setEditSection(null); showSaved(); }}>Kaydet</button>
                                  <button className="stg-btn-danger" onClick={() => { removeCertificate(cert.id); setEditSection(null); }}>Sil</button>
                                  <button className="stg-btn-ghost" onClick={() => setEditSection(null)}>Kapat</button>
                                </div>
                              </div>
                            ) : (
                              <div className="stg-cert-view">
                                <div className="stg-cert-main">
                                  <div className="stg-cert-title-row">
                                    <strong>{cert.name || '(İsimsiz sertifika)'}</strong>
                                    {cert.category && <span className="stg-cert-cat-badge">{cert.category}</span>}
                                    {cert.visibility && cert.visibility !== 'public' && (
                                      <span className="stg-cert-vis-badge">{VISIBILITY_OPTIONS.find(v => v.value === cert.visibility)?.label}</span>
                                    )}
                                    {expiryStatus === 'expired' && <span className="stg-cert-expired-badge">Süresi dolmuş</span>}
                                  </div>
                                  <div className="stg-cert-meta">
                                    {cert.issuer && <span>{cert.issuer}</span>}
                                    {cert.scope && <span>{cert.scope}</span>}
                                    {cert.date && <span>Tarih: {cert.date}</span>}
                                    {cert.expiry && <span>Geçerlilik: {cert.expiry}</span>}
                                    {cert.documentNo && <span>No: {cert.documentNo}</span>}
                                  </div>
                                  {cert.notes && <p className="stg-cert-notes">{cert.notes}</p>}
                                  {(cert.verifyUrl || cert.fileUrl) && (
                                    <div className="stg-cert-links">
                                      {cert.verifyUrl && <a href={cert.verifyUrl} target="_blank" rel="noreferrer">Doğrula &rarr;</a>}
                                      {cert.fileUrl && <a href={cert.fileUrl} target="_blank" rel="noreferrer">Belgeyi Aç &rarr;</a>}
                                    </div>
                                  )}
                                </div>
                                <div className="stg-cert-actions">
                                  <button className="stg-edit-btn" onClick={() => setEditSection(`cert-${cert.id}`)}>
                                    <span>&#9998;</span> Düzenle
                                  </button>
                                  <button className="stg-btn-danger" style={{ padding: '.35rem .65rem', fontSize: '.72rem' }} onClick={() => removeCertificate(cert.id)}>
                                    Kaldır
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* Legacy free-text notes */}
                  <div className="stg-cert-legacy">
                    <div className="stg-cert-legacy-header">
                      <span>Serbest Not / Diğer Eğitimler</span>
                      {editSection !== 'certificates' && (
                        <button className="stg-edit-btn" onClick={() => setEditSection('certificates')}>
                          <span>&#9998;</span> Düzenle
                        </button>
                      )}
                    </div>
                    {editSection === 'certificates' ? (
                      <form onSubmit={saveCertificates}>
                        <textarea rows={3} value={certificates} onChange={e => setCertificates(e.target.value)} placeholder="Yukarıdaki listeye uymayan eğitim, kurs veya notlarınızı buraya yazın..." style={{ width: '100%' }} />
                        <div className="stg-card-actions">
                          <button type="submit" className="stg-btn-primary">Kaydet</button>
                          <button type="button" className="stg-btn-ghost" onClick={() => setEditSection(null)}>İptal</button>
                        </div>
                      </form>
                    ) : (
                      <p className="stg-cert-legacy-text" style={{ whiteSpace: 'pre-line' }}>{certificates || '—'}</p>
                    )}
                  </div>
                </div>

                {/* Licenses & General Certificates Card */}
                <div className="stg-card">
                  <div className="stg-card-header">
                    <div>
                      <h2>Ehliyet & Diğer Belgeler</h2>
                      <p className="stg-card-desc">Sürücü belgesi, yabancı dil, ilk yardım, ISO, yazılım sertifikaları ve operatör belgeleri</p>
                    </div>
                    <div style={{ display: 'flex', gap: '.4rem', flexWrap: 'wrap' }}>
                      <button className="stg-btn-outline" onClick={() => setLicensePicker({ open: true, category: null, search: '' })}>
                        + Listeden Seç
                      </button>
                      <button className="stg-btn-outline" onClick={addLicenseCustom}>
                        + Manuel Ekle
                      </button>
                    </div>
                  </div>

                  {licensePicker.open && (
                    <div className="stg-cert-picker">
                      <div className="stg-cert-picker-toolbar">
                        <input
                          type="text"
                          placeholder="Belge ara... (örn: B sınıfı, IELTS, forklift, ISO 9001)"
                          value={licensePicker.search}
                          onChange={e => setLicensePicker(p => ({ ...p, search: e.target.value }))}
                          className="stg-cert-search"
                          autoFocus
                        />
                        <select
                          value={licensePicker.category || ''}
                          onChange={e => setLicensePicker(p => ({ ...p, category: e.target.value || null }))}
                          className="stg-cert-filter"
                        >
                          <option value="">Tüm kategoriler</option>
                          {LICENSE_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                        </select>
                        <button className="stg-btn-ghost" onClick={() => setLicensePicker({ open: false, category: null, search: '' })}>Kapat</button>
                      </div>
                      <div className="stg-cert-picker-body">
                        {LICENSE_CATEGORIES
                          .filter(cat => !licensePicker.category || cat === licensePicker.category)
                          .map(cat => {
                            const q = licensePicker.search.trim().toLowerCase();
                            const items = GENERAL_LICENSES.filter(c => c.category === cat && (!q || c.name.toLowerCase().includes(q) || (c.scope || '').toLowerCase().includes(q)));
                            if (items.length === 0) return null;
                            return (
                              <div key={cat} className="stg-cert-group">
                                <div className="stg-cert-group-title">{cat}</div>
                                <div className="stg-cert-group-items">
                                  {items.map(preset => (
                                    <button key={preset.id} className="stg-cert-option" onClick={() => addLicenseFromPreset(preset)}>
                                      <span className="stg-cert-option-name">{preset.name}</span>
                                      {preset.scope && <span className="stg-cert-option-issuer">{preset.scope}</span>}
                                    </button>
                                  ))}
                                </div>
                              </div>
                            );
                          })}
                      </div>
                    </div>
                  )}

                  {licenseList.length === 0 && !licensePicker.open ? (
                    <div className="stg-info-banner">
                      <span className="stg-info-banner-icon">&#8505;</span>
                      <span>Henüz belge eklenmedi. Ehliyet, dil sınavı, operatör belgesi vb. için "Listeden Seç" kullanabilirsiniz.</span>
                    </div>
                  ) : (
                    <div className="stg-cert-list">
                      {licenseList.map(lic => {
                        const isEditing = editSection === `lic-${lic.id}`;
                        const expiryStatus = lic.expiry ? (new Date(lic.expiry) < new Date() ? 'expired' : 'valid') : null;
                        return (
                          <div key={lic.id} className={`stg-cert-item${expiryStatus === 'expired' ? ' expired' : ''}`}>
                            {isEditing ? (
                              <div className="stg-cert-edit">
                                <div className="stg-form-grid">
                                  <div className="stg-field full">
                                    <label>Belge Adı *</label>
                                    <input type="text" value={lic.name} onChange={e => updateLicense(lic.id, { name: e.target.value })} placeholder="örn: Sürücü Belgesi B, IELTS 7.5" />
                                  </div>
                                  <div className="stg-field">
                                    <label>Kategori</label>
                                    <select value={lic.category || ''} onChange={e => updateLicense(lic.id, { category: e.target.value })}>
                                      <option value="">Seçin...</option>
                                      {LICENSE_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                                    </select>
                                  </div>
                                  <div className="stg-field">
                                    <label>Veren Kurum</label>
                                    <input type="text" value={lic.issuer || ''} onChange={e => updateLicense(lic.id, { issuer: e.target.value })} placeholder="örn: Emniyet Müd., ETS, ÖSYM" />
                                  </div>
                                  <div className="stg-field full">
                                    <label>Sınıf / Puan / Kapsam</label>
                                    <input type="text" value={lic.scope || ''} onChange={e => updateLicense(lic.id, { scope: e.target.value })} placeholder="örn: B sınıfı, CEFR C1, 85 puan" />
                                  </div>
                                  <div className="stg-field">
                                    <label>Veriliş Tarihi</label>
                                    <input type="date" value={lic.date || ''} onChange={e => updateLicense(lic.id, { date: e.target.value })} />
                                  </div>
                                  <div className="stg-field">
                                    <label>Son Geçerlilik</label>
                                    <input type="date" value={lic.expiry || ''} onChange={e => updateLicense(lic.id, { expiry: e.target.value })} />
                                  </div>
                                  <div className="stg-field">
                                    <label>Belge No</label>
                                    <input type="text" value={lic.documentNo || ''} onChange={e => updateLicense(lic.id, { documentNo: e.target.value })} />
                                  </div>
                                  <div className="stg-field full">
                                    <label>Notlar</label>
                                    <textarea rows={2} value={lic.notes || ''} onChange={e => updateLicense(lic.id, { notes: e.target.value })} />
                                  </div>
                                </div>
                                <div className="stg-card-actions">
                                  <button className="stg-btn-primary" onClick={() => { setEditSection(null); showSaved(); }}>Kaydet</button>
                                  <button className="stg-btn-danger" onClick={() => { removeLicense(lic.id); setEditSection(null); }}>Sil</button>
                                  <button className="stg-btn-ghost" onClick={() => setEditSection(null)}>Kapat</button>
                                </div>
                              </div>
                            ) : (
                              <div className="stg-cert-view">
                                <div className="stg-cert-main">
                                  <div className="stg-cert-title-row">
                                    <strong>{lic.name || '(İsimsiz belge)'}</strong>
                                    {lic.category && <span className="stg-cert-cat-badge">{lic.category}</span>}
                                    {expiryStatus === 'expired' && <span className="stg-cert-expired-badge">Süresi dolmuş</span>}
                                  </div>
                                  <div className="stg-cert-meta">
                                    {lic.issuer && <span>{lic.issuer}</span>}
                                    {lic.scope && <span>{lic.scope}</span>}
                                    {lic.date && <span>Tarih: {lic.date}</span>}
                                    {lic.expiry && <span>Geçerlilik: {lic.expiry}</span>}
                                    {lic.documentNo && <span>No: {lic.documentNo}</span>}
                                  </div>
                                  {lic.notes && <p className="stg-cert-notes">{lic.notes}</p>}
                                </div>
                                <div className="stg-cert-actions">
                                  <button className="stg-edit-btn" onClick={() => setEditSection(`lic-${lic.id}`)}>
                                    <span>&#9998;</span> Düzenle
                                  </button>
                                  <button className="stg-btn-danger" style={{ padding: '.35rem .65rem', fontSize: '.72rem' }} onClick={() => removeLicense(lic.id)}>
                                    Kaldır
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                <div className="stg-info-banner">
                  <span className="stg-info-banner-icon">&#8505;</span>
                  <span>Eğitim bilgileri profilinizde ve CV çıktılarında görünecektir.</span>
                </div>
              </>
            )}

            {/* ── SOCIAL PROFILE ── */}
            {activeTab === 'social' && (
              <>
                <div className={`stg-card stg-accordion ${openAcc.discipline ? 'open' : 'closed'}`}>
                  <div className="stg-card-header stg-accordion-header" onClick={() => toggleAcc('discipline')} role="button" tabIndex={0} onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleAcc('discipline'); } }}>
                    <div>
                      <h2>Disiplin & Sektör Rolü</h2>
                      <p className="stg-card-desc">Toplulukta görünecek profesyonel profiliniz</p>
                    </div>
                    <span className="stg-accordion-caret" aria-hidden="true">▾</span>
                  </div>
                  {openAcc.discipline && (
                    <div className="stg-form-grid">
                      <div className="stg-field">
                        <label>Disiplin</label>
                        <select value={socialProfile.discipline} onChange={e => setSocialProfile(p => ({ ...p, discipline: e.target.value }))}>
                          <option value="">Seçin...</option>
                          {disciplineOptions.map(d => (
                            <option key={d.id} value={d.id}>{d.name}</option>
                          ))}
                        </select>
                      </div>
                      <div className="stg-field">
                        <label>Sektör Rolü</label>
                        <select value={socialProfile.sectorRole} onChange={e => setSocialProfile(p => ({ ...p, sectorRole: e.target.value }))}>
                          <option value="">Seçin...</option>
                          {sectorRoleOptions.map(r => (
                            <option key={r.key} value={r.key}>{r.name}</option>
                          ))}
                        </select>
                      </div>
                      <div className="stg-field">
                        <label>Şehir</label>
                        <AutocompleteInput
                          value={socialProfile.city}
                          onChange={val => setSocialProfile(p => ({ ...p, city: val, ...(val !== p.city ? { district: '' } : {}) }))}
                          options={CITY_LIST}
                          placeholder="İl seçin veya yazın..."
                        />
                      </div>
                      <div className="stg-field">
                        <label>İlçe</label>
                        <AutocompleteInput
                          value={socialProfile.district}
                          onChange={val => setSocialProfile(p => ({ ...p, district: val }))}
                          options={getDistricts(socialProfile.city)}
                          placeholder={socialProfile.city ? 'İlçe seçin veya yazın...' : 'Önce il seçin...'}
                        />
                      </div>
                      <div className="stg-field full">
                        <label>Biyografi</label>
                        <textarea rows={3} value={socialProfile.bio} onChange={e => setSocialProfile(p => ({ ...p, bio: e.target.value }))} placeholder="Kendinizi kısaca tanıtın..." />
                      </div>
                    </div>
                  )}
                </div>

                {/* ── DYNAMIC SECTOR INFO — role'e göre değişir ── */}
                {socialProfile.sectorRole && (() => {
                  const si = socialProfile.sectorInfo || {};
                  const updateSi = (patch) => setSocialProfile(p => ({ ...p, sectorInfo: { ...(p.sectorInfo || {}), ...patch } }));
                  const role = socialProfile.sectorRole;
                  const roleLabels = { muellif: 'Müellif', kontrolor: 'Kontrolör', yuklenici: 'Yüklenici', is_sahibi: 'İş Sahibi', ogrenci: 'Öğrenci' };
                  return (
                    <div className={`stg-card stg-accordion ${openAcc.roleInfo ? 'open' : 'closed'}`}>
                      <div className="stg-card-header stg-accordion-header" onClick={() => toggleAcc('roleInfo')} role="button" tabIndex={0} onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleAcc('roleInfo'); } }}>
                        <div>
                          <h2>{roleLabels[role]} Bilgileri</h2>
                          <p className="stg-card-desc">Rolünüze özel bilgiler — proje paftalarında ve resmi belgelerde kullanılır</p>
                        </div>
                        <span className="stg-accordion-caret" aria-hidden="true">▾</span>
                      </div>
                      {openAcc.roleInfo && (
                      <div className="stg-form-grid">
                        {role === 'muellif' && (<>
                          <div className="stg-field"><label>Unvan</label>
                            <select value={si.title || ''} onChange={e => updateSi({ title: e.target.value })}>
                              <option value="">Seçiniz</option>
                              <option value="Elk. Müh.">Elk. Müh.</option>
                              <option value="Elk. Yük. Müh.">Elk. Yük. Müh.</option>
                              <option value="Elk.-Elk. Müh.">Elk.-Elk. Müh.</option>
                              <option value="Elk. Teknikeri">Elk. Teknikeri</option>
                              <option value="Mimar">Mimar</option>
                              <option value="Mak. Müh.">Mak. Müh.</option>
                            </select>
                          </div>
                          <div className="stg-field"><label>Ad Soyad</label><input type="text" value={si.fullName || ''} onChange={e => updateSi({ fullName: e.target.value })} /></div>
                          <div className="stg-field"><label>Diploma No</label><input type="text" value={si.diplomaNo || ''} onChange={e => updateSi({ diplomaNo: e.target.value })} /></div>
                          <div className="stg-field"><label>Büro Tescil No</label><input type="text" value={si.buroTescilNo || ''} onChange={e => updateSi({ buroTescilNo: e.target.value })} /></div>
                          <div className="stg-field"><label>Oda Sicil No</label><input type="text" value={si.chamberNo || ''} onChange={e => updateSi({ chamberNo: e.target.value })} placeholder="EMO / TMMOB sicil no" /></div>
                          <div className="stg-field"><label>Vergi No / T.C.</label><input type="text" value={si.vergiNo || ''} onChange={e => updateSi({ vergiNo: e.target.value })} /></div>
                          <div className="stg-field"><label>Vergi Dairesi</label><input type="text" value={si.vergiDairesi || ''} onChange={e => updateSi({ vergiDairesi: e.target.value })} /></div>
                          <div className="stg-field"><label>Telefon</label><input type="tel" value={si.phone || ''} onChange={e => updateSi({ phone: e.target.value })} /></div>
                          <div className="stg-field"><label>E-posta</label><input type="email" value={si.email || ''} onChange={e => updateSi({ email: e.target.value })} /></div>
                        </>)}

                        {role === 'kontrolor' && (<>
                          <div className="stg-field"><label>Unvan</label><input type="text" value={si.title || ''} onChange={e => updateSi({ title: e.target.value })} placeholder="örn: Kontrol Mühendisi" /></div>
                          <div className="stg-field"><label>Ad Soyad</label><input type="text" value={si.fullName || ''} onChange={e => updateSi({ fullName: e.target.value })} /></div>
                          <div className="stg-field"><label>Oda Sicil No</label><input type="text" value={si.chamberNo || ''} onChange={e => updateSi({ chamberNo: e.target.value })} /></div>
                          <div className="stg-field"><label>Denetim Firması</label><input type="text" value={si.denetimFirmasi || ''} onChange={e => updateSi({ denetimFirmasi: e.target.value })} placeholder="Yapı Denetim firması" /></div>
                          <div className="stg-field"><label>Denetim Yetki Belgesi No</label><input type="text" value={si.yetkiBelgeNo || ''} onChange={e => updateSi({ yetkiBelgeNo: e.target.value })} /></div>
                          <div className="stg-field"><label>Telefon</label><input type="tel" value={si.phone || ''} onChange={e => updateSi({ phone: e.target.value })} /></div>
                          <div className="stg-field"><label>E-posta</label><input type="email" value={si.email || ''} onChange={e => updateSi({ email: e.target.value })} /></div>
                        </>)}

                        {role === 'yuklenici' && (<>
                          <div className="stg-field"><label>Firma Adı</label><input type="text" value={si.firmaAdi || ''} onChange={e => updateSi({ firmaAdi: e.target.value })} /></div>
                          <div className="stg-field"><label>Yetkili Ad Soyad</label><input type="text" value={si.fullName || ''} onChange={e => updateSi({ fullName: e.target.value })} /></div>
                          <div className="stg-field"><label>Vergi No</label><input type="text" value={si.vergiNo || ''} onChange={e => updateSi({ vergiNo: e.target.value })} /></div>
                          <div className="stg-field"><label>Vergi Dairesi</label><input type="text" value={si.vergiDairesi || ''} onChange={e => updateSi({ vergiDairesi: e.target.value })} /></div>
                          <div className="stg-field"><label>Uzmanlık Alanı</label><input type="text" value={si.uzmanlik || ''} onChange={e => updateSi({ uzmanlik: e.target.value })} placeholder="örn: Elektrik tesisatı" /></div>
                          <div className="stg-field"><label>Telefon</label><input type="tel" value={si.phone || ''} onChange={e => updateSi({ phone: e.target.value })} /></div>
                          <div className="stg-field"><label>E-posta</label><input type="email" value={si.email || ''} onChange={e => updateSi({ email: e.target.value })} /></div>
                        </>)}

                        {role === 'is_sahibi' && (<>
                          <div className="stg-field"><label>Ad Soyad / Firma Adı</label><input type="text" value={si.fullName || ''} onChange={e => updateSi({ fullName: e.target.value })} /></div>
                          <div className="stg-field"><label>T.C. / Vergi No</label><input type="text" value={si.tcNo || ''} onChange={e => updateSi({ tcNo: e.target.value })} /></div>
                          <div className="stg-field full"><label>Adres</label><input type="text" value={si.address || ''} onChange={e => updateSi({ address: e.target.value })} /></div>
                          <div className="stg-field"><label>Telefon</label><input type="tel" value={si.phone || ''} onChange={e => updateSi({ phone: e.target.value })} /></div>
                          <div className="stg-field"><label>E-posta</label><input type="email" value={si.email || ''} onChange={e => updateSi({ email: e.target.value })} /></div>
                        </>)}

                        {role === 'ogrenci' && (<>
                          <div className="stg-field"><label>Ad Soyad</label><input type="text" value={si.fullName || ''} onChange={e => updateSi({ fullName: e.target.value })} /></div>
                          <div className="stg-field"><label>Üniversite / Okul</label>
                            <AutocompleteInput
                              value={si.okul || ''}
                              onChange={val => updateSi({ okul: val })}
                              options={UNIVERSITIES}
                              placeholder="Üniversite seçin veya yazın..."
                            />
                          </div>
                          <div className="stg-field"><label>Bölüm</label>
                            <AutocompleteInput
                              value={si.bolum || ''}
                              onChange={val => updateSi({ bolum: val })}
                              options={ENGINEERING_DEPARTMENTS}
                              placeholder="Bölüm seçin veya yazın..."
                            />
                          </div>
                          <div className="stg-field"><label>Sınıf / Yıl</label><input type="text" value={si.sinif || ''} onChange={e => updateSi({ sinif: e.target.value })} placeholder="örn: 3. sınıf" /></div>
                          <div className="stg-field"><label>Öğrenci No</label><input type="text" value={si.ogrenciNo || ''} onChange={e => updateSi({ ogrenciNo: e.target.value })} /></div>
                          <div className="stg-field"><label>Telefon</label><input type="tel" value={si.phone || ''} onChange={e => updateSi({ phone: e.target.value })} /></div>
                          <div className="stg-field"><label>E-posta</label><input type="email" value={si.email || ''} onChange={e => updateSi({ email: e.target.value })} /></div>
                        </>)}
                      </div>
                      )}
                    </div>
                  );
                })()}

                <div className={`stg-card stg-accordion ${openAcc.socialLinks ? 'open' : 'closed'}`}>
                  <div className="stg-card-header stg-accordion-header" onClick={() => toggleAcc('socialLinks')} role="button" tabIndex={0} onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleAcc('socialLinks'); } }}>
                    <div>
                      <h2>Sosyal Baglantilar</h2>
                    </div>
                    <span className="stg-accordion-caret" aria-hidden="true">▾</span>
                  </div>
                  {openAcc.socialLinks && (
                    <div className="stg-form-grid">
                      <div className="stg-field full">
                        <label>LinkedIn URL</label>
                        <input type="url" value={socialProfile.linkedinUrl} onChange={e => setSocialProfile(p => ({ ...p, linkedinUrl: e.target.value }))} placeholder="https://linkedin.com/in/..." />
                      </div>
                      <div className="stg-field full">
                        <label>Twitter / X URL</label>
                        <input type="url" value={socialProfile.twitterUrl} onChange={e => setSocialProfile(p => ({ ...p, twitterUrl: e.target.value }))} placeholder="https://twitter.com/..." />
                      </div>
                      <div className="stg-field full">
                        <label>Web Sitesi</label>
                        <input type="url" value={socialProfile.websiteUrl} onChange={e => setSocialProfile(p => ({ ...p, websiteUrl: e.target.value }))} placeholder="https://..." />
                      </div>
                    </div>
                  )}
                </div>

                <div className={`stg-card stg-accordion ${openAcc.xp ? 'open' : 'closed'}`}>
                  <div className="stg-card-header stg-accordion-header" onClick={() => toggleAcc('xp')} role="button" tabIndex={0} onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleAcc('xp'); } }}>
                    <div>
                      <h2>XP & Seviye</h2>
                    </div>
                    <span className="stg-accordion-caret" aria-hidden="true">▾</span>
                  </div>
                  {openAcc.xp && (<>
                    <div className="stg-display-grid">
                      <div className="stg-display-item">
                        <span className="stg-display-label">XP Puani</span>
                        <span className="stg-display-value" style={{color: 'var(--accent)', fontWeight: 700}}>{user.xp || 0} XP</span>
                      </div>
                      <div className="stg-display-item">
                        <span className="stg-display-label">Seviye</span>
                        <span className="stg-display-value" style={{textTransform:'capitalize'}}>{user.level || 'acemi'}</span>
                      </div>
                    </div>
                    <div style={{padding:'0 1.25rem 1rem', fontSize:'.78rem', color:'var(--muted)'}}>
                      {(user.badges || []).length > 0 ? (
                        <div style={{display:'flex', gap:'.5rem', flexWrap:'wrap', marginTop:'.5rem'}}>
                          {(user.badges || []).map(b => <span key={b} style={{padding:'.2rem .6rem', background:'rgba(167,82,175,.12)', borderRadius:'6px', fontSize:'.75rem'}}>{b}</span>)}
                        </div>
                      ) : 'Henuz rozet kazanilmadi.'}
                    </div>
                  </>)}
                </div>

                <div className={`stg-card stg-accordion ${openAcc.testimonial ? 'open' : 'closed'}`}>
                  <div className="stg-card-header stg-accordion-header" onClick={() => toggleAcc('testimonial')} role="button" tabIndex={0} onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleAcc('testimonial'); } }}>
                    <div>
                      <h2>Anasayfa Yorumum</h2>
                      <p style={{ margin: '.25rem 0 0', fontSize: '.75rem', color: 'var(--muted)' }}>
                        Projabze deneyiminizi anasayfada paylaşmak için yorum bırakın.
                        {testimonial && (
                          <span style={{ marginLeft: 8, padding: '.1rem .5rem', borderRadius: 4, fontSize: '.65rem', fontWeight: 700,
                            background: testimonial.status === 'approved' ? 'rgba(34,197,94,.15)' : testimonial.status === 'rejected' ? 'rgba(239,68,68,.15)' : 'rgba(234,179,8,.15)',
                            color: testimonial.status === 'approved' ? '#22c55e' : testimonial.status === 'rejected' ? '#ef4444' : '#eab308' }}>
                            {testimonial.status === 'approved' ? 'YAYINDA' : testimonial.status === 'rejected' ? 'REDDEDİLDİ' : 'ONAY BEKLİYOR'}
                          </span>
                        )}
                      </p>
                    </div>
                    <span className="stg-accordion-caret" aria-hidden="true">▾</span>
                  </div>
                  {openAcc.testimonial && (
                    <div className="stg-form-grid">
                      <div className="stg-field full">
                        <label>Puan</label>
                        <div style={{ display: 'flex', gap: '.4rem' }}>
                          {[1, 2, 3, 4, 5].map(n => (
                            <button
                              key={n}
                              type="button"
                              onClick={() => setTestimonialDraft(d => ({ ...d, stars: n }))}
                              style={{
                                background: 'transparent',
                                border: 'none',
                                fontSize: '1.5rem',
                                cursor: 'pointer',
                                color: n <= testimonialDraft.stars ? '#fbbf24' : 'rgba(255,255,255,.2)',
                                padding: 0,
                              }}
                            >★</button>
                          ))}
                        </div>
                      </div>
                      <div className="stg-field full">
                        <label>Yorumunuz <span style={{ color: 'var(--muted)', fontSize: '.7rem' }}>({testimonialDraft.text.length}/600)</span></label>
                        <textarea
                          value={testimonialDraft.text}
                          maxLength={600}
                          rows={4}
                          onChange={e => setTestimonialDraft(d => ({ ...d, text: e.target.value }))}
                          placeholder="Projabze ile deneyiminizi birkaç cümleyle anlatın..."
                          style={{ resize: 'vertical', minHeight: 90, fontFamily: 'inherit' }}
                        />
                      </div>
                      <div className="stg-field full">
                        <label style={{ display: 'flex', alignItems: 'flex-start', gap: '.55rem', cursor: 'pointer', fontWeight: 'normal', fontSize: '.78rem', lineHeight: 1.4 }}>
                          <input
                            type="checkbox"
                            checked={testimonialDraft.publishConsent}
                            onChange={e => setTestimonialDraft(d => ({ ...d, publishConsent: e.target.checked }))}
                            style={{ marginTop: 2, flexShrink: 0 }}
                          />
                          <span>
                            Adımın, rolüm/şehrim ve yorumumun Projabze ana sayfasında yayınlanmasına <strong>açık rıza</strong> veriyorum.
                            Rızamı dilediğim zaman geri çekebileceğimi ve bu durumda yorumumun kaldırılacağını biliyorum.
                            Detaylar için <a href="/privacy?tab=riza#testimonial" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--accent)' }}>Açık Rıza Metni</a>'ne bakın.
                          </span>
                        </label>
                      </div>
                      {testimonialMsg && (
                        <div className="stg-field full" style={{ fontSize: '.78rem', color: testimonialMsg.includes('hata') || testimonialMsg.includes('gerekli') ? '#ef4444' : 'var(--muted)' }}>
                          {testimonialMsg}
                        </div>
                      )}
                      <div className="stg-field full" style={{ display: 'flex', gap: '.5rem' }}>
                        <button
                          type="button"
                          className="stg-btn-primary"
                          disabled={testimonialBusy || !testimonialDraft.publishConsent || !testimonialDraft.text.trim()}
                          onClick={submitTestimonial}
                        >
                          {testimonialBusy ? 'Gönderiliyor...' : (testimonial ? 'Yorumu Güncelle' : 'Yorumu Gönder')}
                        </button>
                        {testimonial && (
                          <button
                            type="button"
                            className="stg-btn-secondary"
                            onClick={deleteTestimonial}
                          >
                            Yorumu Kaldır
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                <div className="stg-card-actions" style={{padding:'0 0 1rem'}}>
                  <button className="stg-btn-primary" disabled={socialSaving} onClick={async () => {
                    setSocialSaving(true);
                    try {
                      const token = localStorage.getItem('projabze_token');
                      const res = await fetch(`${API_URL}/api/social/users/me/profile`, {
                        method: 'PATCH',
                        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
                        body: JSON.stringify(socialProfile)
                      });
                      const data = await res.json();
                      if (res.ok) {
                        const updatedUser = { ...user, ...data.user };
                        setUser(updatedUser);
                        localStorage.setItem('projabze_user', JSON.stringify(updatedUser));
                        showSaved();
                      }
                    } catch (e) { console.error(e); }
                    setSocialSaving(false);
                  }}>{socialSaving ? 'Kaydediliyor...' : 'Sosyal Profili Kaydet'}</button>
                </div>

                <div className="stg-info-banner">
                  <span className="stg-info-banner-icon">&#8505;</span>
                  <span>Sektor rolu ve biyografi bilgileri profilinizde ve feed paylasimlarina gorunecektir.</span>
                </div>
              </>
            )}

            {/* ── CV / ÖZGEÇMİŞ ── */}
            {activeTab === 'cv' && (() => {
              const disciplineLabel = disciplineOptions.find(d => d.id === socialProfile.discipline)?.name;
              const roleLabel = sectorRoleOptions.find(r => r.key === socialProfile.sectorRole)?.name;
              const location = [socialProfile.district, socialProfile.city].filter(Boolean).join(' / ');
              const fullName = `${account.firstName || ''} ${account.lastName || ''}`.trim();
              const hasEducation = educationList.length > 0;
              const hasExperience = experienceList.length > 0;
              const hasLicenses = licenseList.length > 0;
              const hasSkills = skillList.length > 0;
              const hasProjectTypes = projectTypeList.length > 0;
              return (
                <>
                  <div className="stg-card stg-cv-card">
                    <div className="stg-card-header">
                      <div>
                        <h2>Özgeçmiş</h2>
                        <p className="stg-card-desc">Ayarlardaki tüm bilgilerinizden otomatik oluşturuldu. Güncellemek için ilgili sekmeden düzenleyin.</p>
                      </div>
                      <button className="stg-btn-outline" onClick={() => window.print()}>
                        Yazdır / PDF
                      </button>
                    </div>

                    <div className="stg-cv-body">
                      {/* HEADER */}
                      <div className="stg-cv-head">
                        <h1 className="stg-cv-name">{fullName || 'İsimsiz Kullanıcı'}</h1>
                        {(roleLabel || disciplineLabel) && (
                          <p className="stg-cv-role">{[roleLabel, disciplineLabel].filter(Boolean).join(' · ')}</p>
                        )}
                        <div className="stg-cv-contact">
                          {account.email && <span>{account.email}</span>}
                          {account.phone && <span>{account.phone}</span>}
                          {location && <span>{location}</span>}
                          {account.birthDate && <span>Doğum: {account.birthDate}</span>}
                        </div>
                        {(socialProfile.linkedinUrl || socialProfile.twitterUrl || socialProfile.websiteUrl) && (
                          <div className="stg-cv-links">
                            {socialProfile.linkedinUrl && <a href={socialProfile.linkedinUrl} target="_blank" rel="noreferrer">LinkedIn</a>}
                            {socialProfile.twitterUrl && <a href={socialProfile.twitterUrl} target="_blank" rel="noreferrer">X / Twitter</a>}
                            {socialProfile.websiteUrl && <a href={socialProfile.websiteUrl} target="_blank" rel="noreferrer">Web</a>}
                          </div>
                        )}
                      </div>

                      {/* BIO */}
                      {socialProfile.bio && (
                        <section className="stg-cv-section">
                          <h3>Hakkımda</h3>
                          <p className="stg-cv-bio">{socialProfile.bio}</p>
                        </section>
                      )}

                      {/* EXPERIENCE */}
                      {hasExperience && (
                        <section className="stg-cv-section">
                          <h3>Deneyim</h3>
                          <ul className="stg-cv-edu-list">
                            {experienceList.map(exp => (
                              <li key={exp.id} className="stg-cv-edu-item">
                                <div className="stg-cv-edu-head">
                                  <strong>{exp.position || '—'}{exp.company ? ` · ${exp.company}` : ''}</strong>
                                  <span className="stg-cv-year">
                                    {exp.startDate || '—'}{' → '}{exp.current ? 'Devam' : (exp.endDate || '—')}
                                  </span>
                                </div>
                                <div className="stg-cv-edu-sub">
                                  {exp.location && <span>{exp.location}</span>}
                                </div>
                                {exp.description && <p className="stg-cv-bio" style={{ marginTop: '.35rem', fontSize: '.82rem' }}>{exp.description}</p>}
                              </li>
                            ))}
                          </ul>
                        </section>
                      )}

                      {/* PROJECT TYPES */}
                      {hasProjectTypes && (
                        <section className="stg-cv-section">
                          <h3>Proje Tecrübeleri</h3>
                          <ul className="stg-cv-cert-list">
                            {projectTypeList.map(pt => (
                              <li key={pt.id} className="stg-cv-cert-item">
                                <div className="stg-cv-cert-head">
                                  <strong>{pt.name}</strong>
                                  {(pt.count || pt.years) && (
                                    <span className="stg-cv-year">
                                      {pt.count ? `${pt.count} proje` : ''}{pt.count && pt.years ? ' · ' : ''}{pt.years ? `${pt.years} yıl` : ''}
                                    </span>
                                  )}
                                </div>
                                {pt.category && <div className="stg-cv-cert-sub"><span>{pt.category}</span></div>}
                                {pt.notes && <p className="stg-cv-cert-notes">{pt.notes}</p>}
                              </li>
                            ))}
                          </ul>
                        </section>
                      )}

                      {/* SKILLS */}
                      {hasSkills && (
                        <section className="stg-cv-section">
                          <h3>Yazılım & Teknik Yetkinlikler</h3>
                          <div className="stg-cv-skill-chips">
                            {skillList.map(s => {
                              const levelLabel = SKILL_LEVELS.find(l => l.value === s.level)?.label || 'Orta';
                              return (
                                <span key={s.id} className={`stg-cv-skill-chip stg-skill-lv-${s.level || 'intermediate'}`}>
                                  <span className="stg-skill-name">{s.name}</span>
                                  <span className="stg-skill-level">{levelLabel}</span>
                                  {s.years && <span className="stg-skill-years">{s.years} yıl</span>}
                                </span>
                              );
                            })}
                          </div>
                        </section>
                      )}

                      {/* EDUCATION */}
                      {hasEducation && (
                        <section className="stg-cv-section">
                          <h3>Eğitim</h3>
                          <ul className="stg-cv-edu-list">
                            {educationList.map((edu, idx) => {
                              const levelLabel = eduLevels.find(l => l.id === edu.level)?.label || edu.level;
                              return (
                                <li key={idx} className="stg-cv-edu-item">
                                  <div className="stg-cv-edu-head">
                                    <strong>{edu.school || '—'}</strong>
                                    {edu.graduationYear && <span className="stg-cv-year">{edu.graduationYear}</span>}
                                  </div>
                                  <div className="stg-cv-edu-sub">
                                    <span>{levelLabel}</span>
                                    {edu.faculty && <span>· {edu.faculty}</span>}
                                    {edu.department && <span>· {edu.department}</span>}
                                    {edu.gpa && <span>· GNO: {edu.gpa}</span>}
                                  </div>
                                </li>
                              );
                            })}
                          </ul>
                        </section>
                      )}

                      {/* CERTIFICATES (structured) */}
                      {certificateList.length > 0 && (
                        <section className="stg-cv-section">
                          <h3>Sertifikalar & Yetki Belgeleri</h3>
                          <ul className="stg-cv-cert-list">
                            {certificateList.filter(c => (c.visibility || 'public') !== 'private').map(cert => (
                              <li key={cert.id} className="stg-cv-cert-item">
                                <div className="stg-cv-cert-head">
                                  <strong>{cert.name}</strong>
                                  {(cert.date || cert.expiry) && (
                                    <span className="stg-cv-year">
                                      {cert.date || ''}{cert.date && cert.expiry ? ' — ' : ''}{cert.expiry || ''}
                                    </span>
                                  )}
                                </div>
                                <div className="stg-cv-cert-sub">
                                  {cert.issuer && <span>{cert.issuer}</span>}
                                  {cert.scope && <span>· {cert.scope}</span>}
                                  {cert.category && <span>· {cert.category}</span>}
                                  {cert.documentNo && <span>· Belge No: {cert.documentNo}</span>}
                                </div>
                                {cert.notes && <p className="stg-cv-cert-notes">{cert.notes}</p>}
                                {cert.verifyUrl && (
                                  <a className="stg-cv-cert-verify" href={cert.verifyUrl} target="_blank" rel="noreferrer">Doğrula &rarr;</a>
                                )}
                              </li>
                            ))}
                          </ul>
                        </section>
                      )}

                      {/* LICENSES & OTHER DOCS */}
                      {hasLicenses && (
                        <section className="stg-cv-section">
                          <h3>Ehliyet & Diğer Belgeler</h3>
                          <ul className="stg-cv-cert-list">
                            {licenseList.map(lic => (
                              <li key={lic.id} className="stg-cv-cert-item">
                                <div className="stg-cv-cert-head">
                                  <strong>{lic.name}</strong>
                                  {(lic.date || lic.expiry) && (
                                    <span className="stg-cv-year">
                                      {lic.date || ''}{lic.date && lic.expiry ? ' — ' : ''}{lic.expiry || ''}
                                    </span>
                                  )}
                                </div>
                                <div className="stg-cv-cert-sub">
                                  {lic.issuer && <span>{lic.issuer}</span>}
                                  {lic.scope && <span>· {lic.scope}</span>}
                                  {lic.category && <span>· {lic.category}</span>}
                                  {lic.documentNo && <span>· No: {lic.documentNo}</span>}
                                </div>
                                {lic.notes && <p className="stg-cv-cert-notes">{lic.notes}</p>}
                              </li>
                            ))}
                          </ul>
                        </section>
                      )}

                      {/* CERTIFICATES (legacy free-text) */}
                      {certificates && certificates.trim() && (
                        <section className="stg-cv-section">
                          <h3>Diğer Eğitimler</h3>
                          <p className="stg-cv-bio" style={{ whiteSpace: 'pre-line' }}>{certificates}</p>
                        </section>
                      )}

                      {/* EMPTY STATE */}
                      {!fullName && !socialProfile.bio && !hasEducation && !hasExperience && !hasLicenses && !hasSkills && !hasProjectTypes && certificateList.length === 0 && (
                        <div className="stg-info-banner">
                          <span className="stg-info-banner-icon">&#8505;</span>
                          <span>Özgeçmiş bilgisi yok. Diğer sekmelerden (Hesap, Sosyal Profil, Firma, Eğitim) bilgileri doldurun, burada otomatik görünecek.</span>
                        </div>
                      )}
                    </div>
                  </div>
                </>
              );
            })()}

            {/* ── SECURITY ── */}
            {activeTab === 'security' && (
              <>
                {/* Password Card */}
                <div className="stg-card">
                  <div className="stg-card-header">
                    <h2>Şifre</h2>
                    {editSection !== 'password' && (
                      <button className="stg-edit-btn" onClick={() => setEditSection('password')}>
                        <span>&#9998;</span> Degistir
                      </button>
                    )}
                  </div>
                  {editSection === 'password' ? (
                    <form onSubmit={savePassword}>
                      <div className="stg-form-grid single">
                        <div className="stg-field full">
                          <label>Mevcut Şifre</label>
                          <div className="stg-pw-wrapper">
                            <input
                              type={showPw.current ? 'text' : 'password'}
                              value={passwords.current}
                              onChange={e => setPasswords({ ...passwords, current: e.target.value })}
                              placeholder="Mevcut şifreniz"
                            />
                            <button type="button" className="stg-pw-toggle" onClick={() => setShowPw({ ...showPw, current: !showPw.current })}>
                              {showPw.current ? (
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                              ) : (
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                              )}
                            </button>
                          </div>
                        </div>
                        <div className="stg-field full">
                          <label>Yeni Şifre</label>
                          <div className="stg-pw-wrapper">
                            <input
                              type={showPw.newPass ? 'text' : 'password'}
                              value={passwords.newPass}
                              onChange={e => setPasswords({ ...passwords, newPass: e.target.value })}
                              placeholder="En az 6 karakter"
                            />
                            <button type="button" className="stg-pw-toggle" onClick={() => setShowPw({ ...showPw, newPass: !showPw.newPass })}>
                              {showPw.newPass ? (
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                              ) : (
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                              )}
                            </button>
                          </div>
                        </div>
                        <div className="stg-field full">
                          <label>Yeni Şifre (Tekrar)</label>
                          <div className="stg-pw-wrapper">
                            <input
                              type={showPw.confirm ? 'text' : 'password'}
                              value={passwords.confirm}
                              onChange={e => setPasswords({ ...passwords, confirm: e.target.value })}
                              placeholder="Yeni şifrenizi tekrarlayın"
                            />
                            <button type="button" className="stg-pw-toggle" onClick={() => setShowPw({ ...showPw, confirm: !showPw.confirm })}>
                              {showPw.confirm ? (
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                              ) : (
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                              )}
                            </button>
                          </div>
                        </div>
                      </div>
                      <div className="stg-card-actions">
                        <button type="submit" className="stg-btn-primary">Şifreyi Güncelle</button>
                        <button type="button" className="stg-btn-ghost" onClick={() => { setEditSection(null); setPasswords({ current: '', newPass: '', confirm: '' }); }}>İptal</button>
                      </div>
                    </form>
                  ) : (
                    <div className="stg-display-grid">
                      <div className="stg-display-item full">
                        <span className="stg-display-label">Şifre</span>
                        <span className="stg-display-value">&bull;&bull;&bull;&bull;&bull;&bull;&bull;&bull;&bull;&bull;</span>
                      </div>
                    </div>
                  )}
                </div>

                {/* Sessions Card */}
                <div className="stg-card">
                  <div className="stg-card-header">
                    <h2>Oturum Yonetimi</h2>
                  </div>
                  <div className="stg-session">
                    <div className="stg-session-item">
                      <div className="stg-session-device">
                        <span className="stg-session-device-icon">&#128187;</span>
                        <div>
                          <strong>Bu Cihaz</strong>
                          <p>Aktif oturum &middot; {new Date().toLocaleDateString('tr-TR')}</p>
                        </div>
                      </div>
                      <span className="stg-session-active-dot"></span>
                    </div>
                  </div>
                  <div className="stg-card-actions">
                    <button type="button" className="stg-btn-outline" onClick={handleLogout}>
                      Tum Oturumlari Kapat
                    </button>
                  </div>
                </div>

                {/* Danger Card */}
                <div className="stg-card stg-card-danger">
                  <div className="stg-card-header">
                    <h2>Tehlikeli Bölge</h2>
                  </div>
                  <div className="stg-danger-row">
                    <div>
                      <strong>Hesabı Sil</strong>
                      <p>Bu işlem geri alınamaz. KVKK md. 11/e kapsamında silme talebi için <a href="#" onClick={e => { e.preventDefault(); setActiveTab('data'); }}>Hesap Verilerim</a> sekmesine gidin.</p>
                    </div>
                    <button type="button" className="stg-btn-danger" onClick={() => setActiveTab('data')}>Silme Talebi</button>
                  </div>
                </div>
              </>
            )}

            {activeTab === 'data' && (
              <>
                <div className="stg-card">
                  <div className="stg-card-header">
                    <h2>KVKK Hakları</h2>
                  </div>
                  <p style={{ fontSize: 13, color: 'var(--muted)', margin: '0 0 12px' }}>
                    KVKK md. 11 kapsamında kişisel verilerinize ilişkin haklarınız (öğrenme,
                    erişme, düzeltme, silme, aktarımı talep etme) bu sayfadan kullanılabilir.
                    İşlemler en geç 30 gün içinde sonuçlandırılır ve audit log'a kaydedilir.
                  </p>
                </div>

                <div className="stg-card">
                  <div className="stg-card-header">
                    <h2>Verilerimi İndir</h2>
                  </div>
                  <p style={{ fontSize: 13, color: 'var(--muted)' }}>
                    Profilinizde, projelerinizde, paylaşımlarınızda ve audit kayıtlarınızda
                    bulunan tüm kişisel verileri JSON dosyası olarak indirin (KVKK md. 11/d).
                  </p>
                  <div className="stg-card-actions">
                    <button type="button" className="stg-btn-primary" onClick={downloadMyData} disabled={dataExportBusy}>
                      {dataExportBusy ? 'Hazırlanıyor…' : 'JSON Olarak İndir'}
                    </button>
                  </div>
                </div>

                <div className="stg-card">
                  <div className="stg-card-header">
                    <h2>Son Erişim Kayıtlarım ({myAuditTotal})</h2>
                  </div>
                  <p style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 8 }}>
                    Hesabınız üzerinden gerçekleştirilen son işlemler. Şüpheli bir aktivite
                    görürseniz şifrenizi değiştirin ve destek ile iletişime geçin.
                  </p>
                  {myAuditLogs.length === 0 ? (
                    <p style={{ fontSize: 12, color: 'var(--muted)' }}>Henüz kayıt yok.</p>
                  ) : (
                    <div style={{ maxHeight: 320, overflowY: 'auto', border: '1px solid var(--border)', borderRadius: 4 }}>
                      <table className="adm-table" style={{ fontSize: 12, margin: 0 }}>
                        <thead>
                          <tr>
                            <th>Zaman</th>
                            <th>İşlem</th>
                            <th>Endpoint</th>
                            <th>IP</th>
                          </tr>
                        </thead>
                        <tbody>
                          {myAuditLogs.map(l => (
                            <tr key={l.id}>
                              <td style={{ whiteSpace: 'nowrap', color: 'var(--muted)' }}>
                                {new Date(l.occurredAt).toLocaleString('tr-TR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                              </td>
                              <td style={{ fontFamily: 'monospace' }}>{l.action}</td>
                              <td style={{ fontFamily: 'monospace', maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={l.endpoint}>
                                {l.httpMethod} {l.endpoint}
                              </td>
                              <td style={{ fontFamily: 'monospace', color: 'var(--muted)' }}>{l.ipAddressMasked || '—'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>

                <div className="stg-card stg-card-danger">
                  <div className="stg-card-header">
                    <h2>Hesabımı Sil (KVKK md. 11/e)</h2>
                  </div>
                  <p style={{ fontSize: 13, color: 'var(--muted)' }}>
                    Silme talebiniz admin onayı sonrası işlenir. Onaylandığında profil bilgileriniz,
                    paylaşımlarınız ve projeleriniz kalıcı olarak silinir; mali kayıtlar
                    (faturalar) ve audit log gibi yasal saklama gerektiren kayıtlar
                    pseudonimize edilerek yasal süre kadar tutulur.
                  </p>
                  {erasureRequests.length > 0 && erasureRequests[0].status === 'pending' ? (
                    <div style={{ padding: 10, background: 'rgba(234,179,8,.08)', border: '1px solid #eab308', borderRadius: 4, fontSize: 12 }}>
                      <strong>Bekleyen talebiniz var.</strong> Talep tarihi: {new Date(erasureRequests[0].requestedAt).toLocaleString('tr-TR')}.
                      En geç 30 gün içinde sonuçlandırılacaktır.
                    </div>
                  ) : (
                    <>
                      <div className="stg-field full" style={{ marginTop: 8 }}>
                        <label>Silme nedeni (opsiyonel)</label>
                        <textarea
                          rows={3}
                          value={erasureReason}
                          onChange={e => setErasureReason(e.target.value.slice(0, 500))}
                          placeholder="Verilerinizi silmek istediğiniz nedeni paylaşırsanız, sürecimizi iyileştirmemize yardımcı olur."
                          style={{ resize: 'vertical' }}
                        />
                      </div>
                      <div className="stg-card-actions">
                        <button type="button" className="stg-btn-danger" onClick={submitErasureRequest} disabled={erasureBusy}>
                          {erasureBusy ? 'Gönderiliyor…' : 'Silme Talebi Gönder'}
                        </button>
                      </div>
                    </>
                  )}
                  {erasureMsg && (
                    <p style={{ marginTop: 8, fontSize: 12, color: erasureMsg.includes('alındı') ? '#22c55e' : '#ef4444' }}>
                      {erasureMsg}
                    </p>
                  )}
                </div>
              </>
            )}

          </div>
        </div>
      </main>

      {/* ─── RIGHT SIDEBAR ─── */}
      <RightSidebar />
    </div>
  );
};

export default Settings;
